import type { NotificationPreview, ContactCardDto, ChatFolderDto, ChatFolderInput, ConversationDto, CreatePollInput, LocationPoint, ScheduledMessageDto, MessageChangesDto, MessageDto, PresenceDto, ReminderDto, RsvpAnswer, NotifyLevel, CreateChatEventInput, ServerEvent, StarredMessageDto } from '@dijitalapp/shared';
import { api, ApiError, type ClientErrorCode } from './api';
import { uploadAttachment, type UploadOptions } from './upload';
import { CallManager } from './calls';
import { GroupCallManager } from './groupCalls';
import { loadAppearance } from './appearance';
import { attention, canNotify, chime, lookingElsewhere, photoIcon, setTitleUnread, showLocal } from './notify';
import { CONTACT_MSGTYPE, E2ee, EVENT_MSGTYPE, POLL_MSGTYPE, type DecryptedContent, type EncryptedAnswer } from './e2ee';
import type { E2eeFileMeta } from './outbox';
import { outbox, uuidv7, type QueuedMessage } from './outbox';
import { RealtimeClient, type ConnectionState } from './realtime';
import { forgetBadges, hydrateBadges, resetBadges, snapshotBadges } from './badges';
import { photoPreview, videoPreview } from './mediaThumb';
import { doNotDisturb } from './myPresence';
import { describeDecrypted, describePlain, type PreviewTexts } from './notifyPreview';
import { makePushPreviews, syncPreviewKey } from './pushPreview';
import { cardFromPreview, type EncryptedLinkCard } from './linkCard';
import { failureOf, fetchBytes, forwardItem, forwardRoute, type ForwardFailure, type ForwardFileMeta, type ForwardFileSource, type ForwardItem, type ForwardOutcome, type SendSettled } from './forwarding';
import { loadChats, saveChats, snapshotChats } from './offlineCache';
import { noteConnected } from './connectionLabel';
import { onResume, setReloadGuard } from './resume';
import { refreshAvatar } from '../components/UserAvatar';
import { byActivity, unreadMessages } from './chatList';
import { adjustCounts, countedReactions } from './reactions';

export type PendingStatus = 'queued' | 'sending' | 'failed';

export interface PendingMessage extends QueuedMessage {
  status: PendingStatus;
  error?: ClientErrorCode;
}

export interface ChatState {
  conversations: ConversationDto[];
  loaded: boolean;
  messages: Record<string, MessageDto[]>;
  hasMore: Record<string, boolean>;
  pending: Record<string, PendingMessage[]>;
  typing: Record<string, boolean>;
  /** By user id; only users whose presence the server lets us see. */
  presence: Record<string, PresenceDto>;
  connection: ConnectionState;
  /** Ids of the user's starred messages (private bookmarks). */
  starred: ReadonlySet<string>;
  /** My pending "remind me" reminders: message id → when (ISO). */
  reminders: ReadonlyMap<string, string>;
  /** Bumped when reminders change on the server (set elsewhere, fired), so open lists refetch. */
  remindersVersion: number;
  /** End-to-end encryption is set up on this device. */
  e2eeReady: boolean;
  /** Bumped when someone I see changes their photo, name or about line (open profiles refetch). */
  profileVersion: number;
  /** Setting it up failed even after retries (the reason, for the "try again" banner); null otherwise. */
  e2eeFailed: string | null;
  /** Bumped when new room keys arrive (views retry undecryptable messages). */
  e2eeVersion: number;
  /** Bumped when calendar events or tasks in one of the user's scopes change (views refetch). */
  plannerVersion: number;
  /** Bumped when a call starts, is answered or ends: chats show their call records. */
  callsVersion: number;
  /** Bumped when classes or homework change (education views refetch). */
  learnVersion: number;
  /** Bumped when someone I can see posts or removes a story (the stories strip refetches). */
  storiesVersion: number;
  /** My chat folders, in my order. */
  folders: ChatFolderDto[];
  /** Bumped when my scheduled messages change (the scheduled list refetches). */
  scheduledVersion: number;
}

const TYPING_TTL_MS = 6_000;

function mergeMessages(existing: MessageDto[] = [], incoming: MessageDto[]): MessageDto[] {
  const byId = new Map(existing.map((m) => [m.id, m]));
  for (const m of incoming) byId.set(m.id, m);
  return [...byId.values()].sort((a, b) => a.seq - b.seq);
}

/** How long a hole in a chat may wait for its messages to arrive on their own (one sent a moment earlier, overtaken). */
const GAP_GRACE_MS = 400;
/** Pages of up to 200 messages one gap fill reads at most (anything further comes with the next sync). */
const GAP_MAX_PAGES = 10;
/** The open chat's newest messages are tried again after these pauses when the connection fails. */
const OPEN_RETRY_MS = [1_000, 2_000, 4_000];
/** A fetch of a chat's messages under way longer than this is not waited for when the chat must show its newest now. */
const STALE_OPEN_MS = 3_000;
/** A catch-up that failed as a whole (the list or the server time) is tried again after this while connected. */
const SYNC_RETRY_MS = 5_000;
/**
 * The reads that bring the newest messages and the list (opening a chat, the catch-up): given up sooner than other
 * calls, so one stuck on a connection that died is asked again instead of holding the chat on old messages.
 */
const LIVE_READ = { timeoutMs: 8_000 } as const;

/** The first hole in a chat's messages on this device: [the seq before it, the seq after it]; null when there is none. */
export function firstHole(list: readonly MessageDto[] | undefined): [number, number] | null {
  if (!list) return null;
  for (let i = 1; i < list.length; i++) if (list[i]!.seq > list[i - 1]!.seq + 1) return [list[i - 1]!.seq, list[i]!.seq];
  return null;
}

/**
 * A chat as the server listed it, against the one on hand: an answer read before the latest messages arrived over
 * the socket must not take their preview, position and unread count back.
 */
export function fresherConversation(server: ConversationDto, current: ConversationDto | undefined, known: readonly MessageDto[] | undefined, myId: string): ConversationDto {
  if (!current || current.lastSeq <= server.lastSeq || !current.lastMessage || current.lastMessage.seq <= server.lastSeq) return server;
  // What arrived after the server's answer and counts as unread: from others, in the main timeline, not yet read.
  const read = Math.max(server.myLastReadSeq, server.lastSeq);
  const newer = known?.filter((m) => m.seq > read && m.senderId !== myId && !m.threadRootId).length;
  return {
    ...server,
    lastSeq: current.lastSeq,
    lastMessage: current.lastMessage,
    unreadCount: newer === undefined ? Math.max(server.unreadCount, current.unreadCount) : server.unreadCount + newer,
  };
}

/** Newest activity first: the last message, or when I joined a chat that has none yet (as the server orders it). */
function sortConversations(list: ConversationDto[]): ConversationDto[] {
  return [...list].sort(byActivity);
}

/**
 * Client chat state. Server events and REST results are merged idempotently (by message id and
 * clientMsgId), so duplicates and out-of-order delivery are harmless; seq gaps trigger a fetch.
 */

/** What "Send to a chat" sends: a file, a place or a text (a link), with an optional caption. */
export type ShareToChat = ({ file: Blob; name: string } | { location: LocationPoint } | { text: string }) & { caption?: string };

export interface SendAttachmentOptions {
  caption?: string;
  voice?: boolean;
  durationMs?: number;
  viewOnce?: boolean;
  round?: boolean;
  waveform?: number[];
  /** A photo sent as it is (no re-encoding), shown as a file. */
  asDocument?: boolean;
  /** A picture sent as a sticker (one I made). */
  sticker?: boolean;
  /** A copy forwarded from another chat (the original sender is not named). */
  forwarded?: boolean;
  onProgress?: ((sent: number, total: number) => void) | undefined;
  signal?: AbortSignal | undefined;
}

const uploadOptions = (o: SendAttachmentOptions): UploadOptions => ({
  ...(o.voice ? { voice: true } : {}),
  ...(o.round ? { round: true } : {}),
  ...(o.voice || o.round ? { durationMs: Math.max(1, Math.round(o.durationMs ?? 0)) } : {}),
  ...(o.waveform?.length ? { waveform: o.waveform } : {}),
  ...(o.asDocument ? { asDocument: true } : {}),
  ...(o.sticker ? { sticker: true } : {}),
});

/** The in-app alerts' texts (ChatStore.setAlertTexts). */
export interface AlertTexts {
  newMessage: string;
  newMessageHidden: string;
  newFrom: (name: string) => string;
  brand: string;
  /** "@ Mentioned you ·" before the text. */
  mentioned: string;
  chatUnread: (count: number) => string;
  preview: PreviewTexts;
}

/**
 * Where a chat's catch-up of changes starts: where its last failed one did, else the last sync. A copy kept on
 * this device with no sync time (the app closed before its first sync finished) starts at its oldest message:
 * a server time, and nothing about those messages can have changed before they were written.
 */
export function catchUpFrom(syncedAt: string | null, behind: string | undefined, known: readonly MessageDto[] | undefined): string | null {
  if (behind) return behind;
  if (syncedAt) return syncedAt;
  return known?.reduce<string | null>((oldest, m) => (oldest === null || m.createdAt < oldest ? m.createdAt : oldest), null) ?? null;
}

export class ChatStore {
  /** Server time of the last successful sync: the starting point for catching up on changes. */
  private syncedAt: string | null = null;
  private state: ChatState = {
    conversations: [],
    loaded: false,
    messages: {},
    hasMore: {},
    pending: {},
    typing: {},
    presence: {},
    connection: 'connecting',
    starred: new Set<string>(),
    reminders: new Map<string, string>(),
    remindersVersion: 0,
    e2eeReady: false,
    e2eeFailed: null,
    profileVersion: 0,
    e2eeVersion: 0,
    plannerVersion: 0,
    callsVersion: 0,
    learnVersion: 0,
    storiesVersion: 0,
    folders: [],
    scheduledVersion: 0,
  };
  private e2ee: E2ee | null = null;
  private groupMembers = new Map<string, string[]>();
  /** Forwarding waits for its queued copies: sent, refused (with the reason) or left waiting for the network. */
  private settleWaiters = new Map<string, (o: SendSettled) => void>();
  private listeners = new Set<() => void>();
  /** Chats I deleted from my list, with the last message then: they come back only with a newer message. */
  private removedAt = new Map<string, number>();
  private typingTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private flushing = false;
  /** Asked to send while already sending: go round once more (messages queued meanwhile go out too). */
  private flushAgain = false;
  /** Held back by the server's rate limit: try again shortly instead of waiting for the next send. */
  private rateRetry: ReturnType<typeof setTimeout> | null = null;
  private readonly rt: RealtimeClient;
  /** 1:1 calls; signalling shares this store's realtime connection. */
  readonly calls: CallManager;
  /** Group calls (mesh, up to 6). */
  readonly groupCalls: GroupCallManager;
  /** Conversation currently on screen; incoming messages there are marked read. */
  private activeConversationId: string | null = null;
  private offResume: () => void = () => undefined;

  constructor(
    private readonly myId: string,
    onSessionRevoked: () => void,
  ) {
    this.rt = new RealtimeClient(onSessionRevoked);
    this.calls = new CallManager(this.rt);
    this.groupCalls = new GroupCallManager(this.rt);
    this.groupCalls.me = myId;
    // One call at a time: a group call does not ring during a 1:1 call, and a 1:1 call finds me busy in a group call.
    this.groupCalls.otherCallActive = () => this.calls.view !== null && this.calls.view.phase !== 'ended';
    this.calls.groupBusy = () => this.groupCalls.busy;
    // A new version is never loaded in the middle of a call.
    setReloadGuard(() => (this.calls.view !== null && this.calls.view.phase !== 'ended') || this.groupCalls.busy);
    // Away for a while: badges may have changed meanwhile (the event may have been missed while frozen).
    // And back in front with the connection still up (an iPhone home-screen app thawed before its socket died):
    // nothing reconnects, so the catch-up runs here, as native messengers do the moment they are opened.
    // Reactions, edits and deletions made meanwhile would otherwise never show on this device.
    // Not connected (the socket is being set up again): the open chat and the list come straight over REST, so the
    // newest messages show at once instead of after the reconnect.
    this.offResume = onResume((away) => {
      if (away > 30_000) forgetBadges();
      if (away > 0 && away < 1000) return;
      if (this.rt.state === 'connected') {
        if (away >= 3000) void this.sync();
      } else void this.refreshOpen();
      // Keys sent while this device was frozen (their notice lost with it) are fetched now, and messages still
      // waiting for a key are looked for again.
      if (away >= 3000) void this.e2ee?.sync().catch(() => undefined);
      this.e2ee?.nudgeKeySearch();
    });
    // People added to a 1:1 call: both continue in the new group's call (the group shows in the list too).
    this.calls.onUpgrade = (to, carry) => {
      void this.loadConversations().catch(() => undefined);
      void this.groupCalls.join(to.conversationId, to.title, to.media, carry);
    };
    this.rt.onEvent((e) => {
      this.handle(e);
      if (e.event.startsWith('call.') || e.event === 'error') this.calls.handle(e);
      if (e.event.startsWith('gcall.'))
        this.groupCalls.handle(
          e,
          (id) => this.state.conversations.find((c) => c.id === id)?.group?.title ?? '',
          (id) => this.groupCallRings(id),
        );
    });
    this.rt.onState((connection) => {
      this.set({ connection });
      if (connection === 'connected') {
        noteConnected();
        void this.sync();
        // A call may have started (or ended) while this device was not listening: the open group chat and
        // the calls already known are asked again, so "Join" shows without reopening the chat.
        const active = this.state.conversations.find((c) => c.id === this.activeConversationId);
        if (active?.type === 'group') void this.groupCalls.refresh(active.id);
        this.groupCalls.refreshKnown();
        void this.e2ee?.sync().catch(() => undefined);
      }
    });
  }

  subscribe = (l: () => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };

  /** Every server event, for a Market app listening for its own (e.g. live quizzes); returns the unsubscribe. */
  onServerEvent(l: (e: { event: string; payload: unknown }) => void): () => void {
    return this.rt.onEvent(l);
  }

  /** Sends a Market app's realtime command (Not Defterim's live editing); false while not connected (the app then uses HTTP). */
  sendCommand(cmd: string, payload: Record<string, unknown>, reqId?: string): boolean {
    if (this.rt.state !== 'connected') return false;
    this.rt.command(cmd, payload, reqId);
    return true;
  }

  /** The realtime connection's state changes (a Market app fetches again once it is back). */
  onConnection(l: (s: 'connecting' | 'connected' | 'reconnecting' | 'offline') => void): () => void {
    return this.rt.onState(l);
  }

  getSnapshot = () => this.state;

  private set(patch: Partial<ChatState>) {
    this.state = { ...this.state, ...patch };
    // Message requests wait apart and are not counted until accepted; channels count only where they are listed.
    if (patch.conversations) setTitleUnread(unreadMessages(patch.conversations, this.channelsInChats));
    this.listeners.forEach((l) => l());
  }

  /** Channels shown among my chats (Settings › Sohbetler): only then do their posts count in the tab title and app badge. */
  private channelsInChats = false;
  setChannelsInChats(on: boolean): void {
    if (on === this.channelsInChats) return;
    this.channelsInChats = on;
    setTitleUnread(unreadMessages(this.state.conversations, on));
  }

  // ---------- alerts while the app is open ----------

  /** How much a notification may show (Settings › Notifications): read on start, changed from Settings at once. */
  notificationPreview: NotificationPreview = 'sender';
  /** Texts in the person's language, set by the app. */
  alertText: AlertTexts = {
    newMessage: 'New message',
    newMessageHidden: 'You have a new message',
    newFrom: (name: string) => `${name}: new message`,
    brand: 'DijitalApp',
    mentioned: '@ Mentioned you ·',
    chatUnread: (count: number) => `${count} new messages`,
    preview: {
      newMessage: 'New message',
      photo: '📷 Photo',
      video: '🎬 Video',
      voice: '🎤 Voice message',
      file: '📎 File',
      gif: 'GIF',
      sticker: '🎨 Sticker',
      location: '📍 Location',
      contact: '👤 Contact',
      poll: '📊 Poll:',
      attachment: '📎 Attachment',
    },
  };
  private memberNames = new Map<string, Map<string, string>>();

  setAlertTexts(messages: AlertTexts, calls: CallManager['text'], groupCalls?: GroupCallManager['text']): void {
    this.alertText = messages;
    this.calls.text = calls;
    if (groupCalls) this.groupCalls.text = groupCalls;
  }

  setNotificationPreview(preview: NotificationPreview): void {
    this.notificationPreview = preview;
  }

  private async senderName(c: ConversationDto, senderId: string): Promise<string> {
    if (c.type === 'direct') return c.peer?.displayName ?? '';
    let names = this.memberNames.get(c.id);
    if (!names?.has(senderId)) {
      const { members } = await api<{ members: { id: string; displayName: string }[] }>('GET', `/groups/${c.id}/members`).catch(() => ({ members: [] }));
      names = new Map(members.map((m) => [m.id, m.displayName]));
      this.memberNames.set(c.id, names);
    }
    return names.get(senderId) ?? '';
  }

  /**
   * Whether a message from someone else may alert me in this chat: the push worker's rule (worker.go notifyFor), so
   * the open app and a closed one agree. Named with @, or a reply to one of my messages, gets through a mute and
   * "mentions only"; "none" lets nothing through; a thread reply only reaches the people in that thread.
   */
  private alertsFor(c: ConversationDto, m: MessageDto): boolean {
    if (c.notifyLevel === 'none') return false;
    const loaded = this.state.messages[c.id] ?? [];
    const mentioned = m.mentions?.includes(this.myId) ?? false;
    const replyToMe = m.replyToId !== null && loaded.some((x) => x.id === m.replyToId && x.senderId === this.myId);
    if (mentioned || replyToMe) return true;
    if (c.notifyLevel === 'mentions') return false;
    if (c.mutedUntil && Date.parse(c.mutedUntil) > Date.now()) return false;
    if (m.threadRootId) return loaded.some((x) => (x.id === m.threadRootId || x.threadRootId === m.threadRootId) && x.senderId === this.myId);
    return true;
  }

  /** Whether a group's call may ring here (the push worker's rule for group calls: not muted, notifications "all"). */
  private groupCallRings(conversationId: string): boolean {
    const c = this.state.conversations.find((x) => x.id === conversationId);
    if (!c) return true;
    return c.notifyLevel === 'all' && !(c.mutedUntil && Date.parse(c.mutedUntil) > Date.now());
  }

  /** A notification's first line for a message: its text or kind, decrypted on this device when needed (null: cannot). */
  private async previewLine(m: MessageDto): Promise<string | null> {
    const t = this.alertText;
    let text: string;
    if (m.contentType === 'encrypted') {
      // Another tab of this device runs the crypto now: it would only answer once this tab is in front again.
      if (!this.e2ee?.active) return null;
      const plain = await this.e2ee.decrypt(m).catch(() => null);
      if (!plain) return null;
      text = describeDecrypted(plain.content, t.preview, { reply: Boolean(m.replyToId) });
    } else text = describePlain(m, t.preview);
    return m.mentions?.includes(this.myId) ? `${t.mentioned} ${text}` : text;
  }

  /**
   * The service worker asks for an end-to-end encrypted push's text (public/sw-notify.js askWindows): decrypted
   * here, on the device, and answered only when this person allows full previews of this chat (never a locked chat
   * or a message request). Null otherwise; the push then keeps the server's "New message".
   */
  async pushPreview(conversationId: string, messageId: string, seq?: number): Promise<string | null> {
    if (this.notificationPreview !== 'full') return null;
    const c = this.state.conversations.find((x) => x.id === conversationId);
    if (!c || c.locked || c.request?.incoming) return null;
    let m = this.state.messages[conversationId]?.find((x) => x.id === messageId);
    if (!m && typeof seq === 'number' && seq > 0) {
      const page = await api<{ messages: MessageDto[] }>('GET', `/conversations/${conversationId}/messages?afterSeq=${seq - 1}&limit=1`).catch(() => null);
      m = page?.messages.find((x) => x.id === messageId);
    }
    if (!m || m.senderId === this.myId || m.contentType !== 'encrypted') return null;
    return this.previewLine(m);
  }

  private offWorker: (() => void) | null = null;
  private listenToWorker() {
    const sw = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined;
    if (!sw || this.offWorker) return;
    const onMessage = (e: MessageEvent) => {
      const d = e.data as { type?: string; conversationId?: unknown; messageId?: unknown; seq?: unknown } | null;
      const port = e.ports[0];
      if (!d || d.type !== 'push-preview' || !port) return;
      void this.pushPreview(String(d.conversationId), String(d.messageId), typeof d.seq === 'number' ? d.seq : undefined)
        .catch(() => null)
        .then((line) => port.postMessage({ line }));
    };
    sw.addEventListener('message', onMessage);
    this.offWorker = () => sw.removeEventListener('message', onMessage);
  }

  /**
   * A message from someone else while I am not looking at that chat: the sound chosen for direct or group chats,
   * and a system notification when the app is in the background, presented like the server's push.
   */
  private async alertFor(m: MessageDto): Promise<void> {
    if (m.contentType === 'system') return;
    // Do not disturb: no sound or notification for messages (calls still ring).
    if (doNotDisturb()) return;
    const c = this.state.conversations.find((x) => x.id === m.conversationId);
    if (!c) return;
    if (!this.alertsFor(c, m)) return;
    if (!lookingElsewhere(m.conversationId, this.activeConversationId)) return;
    chime(c.type === 'direct' ? 'message' : 'group');
    if (document.visibilityState === 'visible' && document.hasFocus()) return;
    const t = this.alertText;
    const group = c.type !== 'direct' ? (c.group?.title ?? '') : null;
    const sender = await this.senderName(c, m.senderId);
    // A locked chat never tells who wrote or what, as if previews were hidden.
    const hidden = this.notificationPreview === 'hidden' || c.locked;
    // The tab blinks with who wrote, until I come back (hidden previews show no name).
    attention(`💬 ${hidden ? t.newMessage : t.newFrom(group ?? sender)}`);
    if (!canNotify()) return;
    const title = hidden ? t.brand : group ? (sender ? `${sender} · ${group}` : group) : sender;
    let line = hidden ? t.newMessageHidden : t.newMessage;
    // A message request tells of the person only, never what they wrote, until accepted.
    if (!hidden && this.notificationPreview === 'full' && !c.request?.incoming) line = (await this.previewLine(m)) ?? t.newMessage;
    else if (!hidden && m.mentions?.includes(this.myId)) line = `${t.mentioned} ${t.newMessage}`;
    // Several unread messages in the chat: said under the latest (the notification replaces the chat's previous one).
    const unread = this.state.conversations.find((x) => x.id === c.id)?.unreadCount ?? 0;
    const body = unread > 1 ? `${line}\n${t.chatUnread(unread)}` : line;
    // A hidden preview shows no photo either (it would tell who wrote).
    const photo = hidden
      ? null
      : c.type !== 'direct'
        ? c.group?.avatarUpdatedAt
          ? `/api/v1/groups/${c.id}/avatar?size=sm&v=${Date.parse(c.group.avatarUpdatedAt)}`
          : null
        : `/api/v1/users/${m.senderId}/avatar?size=sm`;
    const icon = photo ? await photoIcon(photo) : null;
    await showLocal(title, { body, tag: `conv-${c.id}`, url: `/messages/${c.id}`, icon });
  }

  /** Settles once this device's copy of the chats has been read (or found missing). */
  private hydrated: Promise<void> = Promise.resolve();

  async start(): Promise<void> {
    this.listenToWorker();
    let hydrated: () => void = () => undefined;
    this.hydrated = new Promise<void>((r) => (hydrated = r));
    // Another account's badges never show for this one (the copy of this account's comes with its chats).
    resetBadges();
    // The list from the server is asked at once, alongside reading this device's copy (no waterfall).
    const fresh = this.loadConversations().catch(() => undefined);
    // Both copies on this device are read together.
    const [queued, cached] = await Promise.all([outbox.all(), loadChats(this.myId).catch(() => null)]).finally(() => setTimeout(hydrated, 0));
    const pending: Record<string, PendingMessage[]> = {};
    for (const q of queued) (pending[q.conversationId] ??= []).push({ ...q, status: 'queued' });
    // The copy kept on this device first: chats open at once, even with no connection. The server then
    // replaces the list, and only messages newer than the copy are fetched (edits and deletions since it too).
    if (cached) {
      // Only fills in what is not here yet: the server's list or a chat opened straight from a link may already
      // have arrived while the copy was being read, and those must win.
      this.syncedAt ??= cached.syncedAt;
      hydrateBadges(cached.badges);
      this.set({
        pending,
        ...(this.state.loaded ? {} : { conversations: sortConversations(cached.conversations), loaded: true }),
        messages: { ...cached.messages, ...this.state.messages },
        hasMore: { ...cached.hasMore, ...this.state.hasMore },
        presence: { ...cached.presence, ...this.state.presence },
      });
    } else this.set({ pending });
    hydrated();
    // The connection and this device's encryption start now, alongside the list from the server (not after it:
    // that was one more round trip, or a hung request, before the socket was even asked for). Sending waits for
    // that first list itself (flush), so nothing goes out before it is known which chats are encrypted.
    this.listed = fresh;
    void this.rt.start();
    void this.startE2ee();
    window.addEventListener('online', this.onOnline);
    // Saved at most every two seconds while things change (a busy chat must not put the save off for ever),
    // and at once when the app goes to the background (a phone may close it there).
    const save = () => {
      if (this.persistTimer) clearTimeout(this.persistTimer);
      this.persistTimer = null;
      void saveChats(this.myId, snapshotChats({ ...this.state, syncedAt: this.syncedAt, badges: snapshotBadges() }));
    };
    const offChange = this.subscribe(() => {
      this.persistTimer ??= setTimeout(save, 2000);
    });
    const due = () => {
      if (this.persistTimer) save();
    };
    const hidden = () => document.visibilityState === 'hidden' && due();
    document.addEventListener('visibilitychange', hidden);
    window.addEventListener('pagehide', due);
    this.persist = () => {
      offChange();
      document.removeEventListener('visibilitychange', hidden);
      window.removeEventListener('pagehide', due);
    };
    await fresh;
    void this.loadStarred().catch(() => undefined);
    void this.loadReminders().catch(() => undefined);
    void this.loadFolders().catch(() => undefined);
    this.resumeLiveShares();
    this.watchIdle();
    this.watchLooking();
  }

  /** Settles once the first list from the server has been asked for (answered or failed). */
  private listed: Promise<void> = Promise.resolve();

  /** Back online: the open chat and the list over REST at once (the socket reconnects by itself). */
  private onOnline = () => {
    if (this.rt.state !== 'connected') void this.refreshOpen();
    this.e2ee?.nudgeKeySearch();
  };

  /**
   * The open chat's newest messages and the chat list, straight from the server whatever the socket is doing
   * (back in front, back online, a notification tapped). The open chat is tried again a few times on a flaky
   * connection; the socket's reconnect then runs the full catch-up as usual.
   */
  private refreshOpen(): Promise<void> {
    const id = this.activeConversationId;
    const list = this.loadConversations().catch(() => undefined);
    const chat = id ? this.openActive(id, true).catch(() => undefined) : Promise.resolve();
    return Promise.all([list, chat]).then(() => undefined);
  }

  /**
   * A notification for this chat was tapped and the app routed to it (no reload): its newest messages come over
   * REST now, and the connection is checked at once instead of at its next retry.
   */
  notificationOpened(conversationId: string): void {
    this.rt.wake();
    void this.loadConversations().catch(() => undefined);
    void this.openConversation(conversationId, { force: true }).catch(() => undefined);
  }

  /**
   * The chat on screen: its newest messages, tried again after 1, 2 and 4 s while it stays open when the
   * connection fails (a refusal such as a locked chat is not tried again). `force`: a fetch already under way for a
   * few seconds (stuck on a dead connection) is not waited for.
   */
  async openActive(id: string, force = false): Promise<void> {
    for (let i = 0; ; i++) {
      try {
        await this.openConversation(id, { force: force || i > 0 });
        return;
      } catch (err) {
        const transient = err instanceof ApiError && (err.code === 'NETWORK' || err.status >= 500);
        if (!transient || i >= OPEN_RETRY_MS.length || this.activeConversationId !== id) throw err;
        await new Promise((r) => setTimeout(r, OPEN_RETRY_MS[i]));
        if (this.activeConversationId !== id) throw err;
      }
    }
  }

  /** Sets up this device's encryption keys in the background (lazy-loads the crypto library). */
  /** Encryption on this device failed to start even after retries: encrypted messages fail visibly. */
  private e2eeGaveUp = false;

  private e2eeStarting = false;
  private profileReload: ReturnType<typeof setTimeout> | undefined;

  private async startE2ee(attempt = 0): Promise<void> {
    this.e2eeStarting = true;
    try {
      const e = await E2ee.start(this.myId);
      if (!e) {
        this.e2eeStarting = false;
        this.e2eeGaveUp = true;
        this.set({ e2eeFailed: 'unsupported' });
        void this.flush();
        return;
      }
      if (this.stopped) {
        void e.stop();
        return;
      }
      this.e2ee = e;
      this.e2eeGaveUp = false;
      this.e2eeStarting = false;
      // Handing the crypto to another tab of this device waits for messages being sent from here (at most 10 s),
      // so nothing typed here is left waiting until this tab is used again.
      e.setBeforeHandOver(async () => {
        for (let waited = 0; this.flushing && waited < 10_000; waited += 100) await new Promise((r) => setTimeout(r, 100));
      });
      // Key requests from other people are answered only in direct chats, to the other person (see
      // E2ee.answerKeyRequests). In groups a later member must not get keys of messages sent before they joined
      // (as in WhatsApp); my own devices still get every key.
      e.setMemberCheck(async (conversationId, userId) => {
        const c = this.state.conversations.find((x) => x.id === conversationId);
        if (!c?.e2ee) return false;
        return c.type === 'direct' && c.peer?.id === userId;
      });
      let parked = false;
      e.subscribe(() => {
        this.set({ e2eeVersion: e.version });
        // Back from another tab of this device: what was typed here meanwhile goes out now.
        if (!e.active) parked = true;
        else if (parked) {
          parked = false;
          void this.flush();
        }
      });
      this.set({ e2eeReady: true, e2eeFailed: null });
      void this.flush();
      // This device's key for encrypted notification previews (lib/pushPreview.ts), signed with its E2EE key.
      this.syncPushPreviewKey();
    } catch (err) {
      // Usually a moment without connection: tried again a few times before giving up.
      console.error('e2ee unavailable on this device', err);
      const delays = [3_000, 10_000, 30_000, 60_000];
      if (attempt < delays.length) setTimeout(() => void this.startE2ee(attempt + 1), delays[attempt]);
      else {
        this.e2eeStarting = false;
        this.e2eeGaveUp = true;
        this.set({ e2eeFailed: err instanceof Error ? `${err.name}: ${err.message}`.slice(0, 200) : String(err).slice(0, 200) });
        void this.flush();
      }
    }
  }

  /** The "try again" of the banner shown when encryption could not start on this device. */
  retryE2ee(): void {
    if (this.e2ee || this.e2eeStarting) return;
    this.set({ e2eeFailed: null });
    void this.startE2ee(0);
  }

  getE2ee(): E2ee | null {
    return this.e2ee;
  }

  private offPushChanged: (() => void) | null = null;
  /** Brings this device's push-preview key in step (also when notifications are turned on later: push.ts). */
  private syncPushPreviewKey(): void {
    const e = this.e2ee;
    if (!e) return;
    void syncPreviewKey({ userId: this.myId, deviceId: e.deviceId, sign: (text) => (e.active ? e.signText(text) : Promise.resolve(null)) });
    if (!this.offPushChanged && typeof window !== 'undefined') {
      const on = () => this.syncPushPreviewKey();
      window.addEventListener('dijitalapp:push-subscribed', on);
      this.offPushChanged = () => window.removeEventListener('dijitalapp:push-subscribed', on);
    }
  }

  /**
   * An encrypted message's notification previews, one per recipient device (made and encrypted here; the server
   * only routes them). Never for view-once media; best effort: none on any trouble, the message goes anyway.
   */
  private async pushPreviewsFor(conv: ConversationDto, clientMsgId: string, content: DecryptedContent, opts: { reply?: boolean; viewOnce?: boolean } = {}) {
    const e = this.e2ee;
    if (!e || !conv.e2ee || opts.viewOnce) return {};
    try {
      const previews = await makePushPreviews(e, { conversationId: conv.id, ref: clientMsgId, from: this.myId, content, ...(opts.reply ? { reply: true } : {}) });
      return previews.length ? { pushPreviews: previews } : {};
    } catch {
      return {};
    }
  }

  /** Irreversible; both people need a device with keys (the server checks). */
  async enableE2ee(conversationId: string): Promise<void> {
    await api('PUT', `/conversations/${conversationId}/e2ee`);
    await this.refreshConversation(conversationId);
  }

  /** Admin flag e2ee_by_default (set by the provider from the public config). */
  private autoEncrypt = false;
  setAutoEncrypt(on: boolean): void {
    this.autoEncrypt = on;
  }
  private autoTried = new Set<string>();

  /**
   * A direct chat that still has no messages switches to end-to-end encryption before its first one, once
   * both people have keys (chats made before the other person had keys). Tried once per chat and session.
   */
  private async maybeAutoEncrypt(conversationId: string): Promise<void> {
    const conv = this.state.conversations.find((c) => c.id === conversationId);
    if (!this.autoEncrypt || !this.e2ee || !conv || conv.type !== 'direct' || conv.e2ee || conv.lastMessage) return;
    if ((this.state.messages[conversationId]?.length ?? 0) > 0 || this.autoTried.has(conversationId)) return;
    this.autoTried.add(conversationId);
    try {
      await this.enableE2ee(conversationId);
    } catch {
      // The other person has no keys yet: this chat stays as it is for now.
    }
  }

  private persist: (() => void) | null = null;
  private idleStop: (() => void) | null = null;
  private lookingStop: (() => void) | null = null;

  /**
   * Whether someone is looking at this window (in front and focused), told to the server at every change and on
   * every connect: notifications to the person's phone and other devices are held back only while they look.
   */
  private watchLooking(): void {
    let told: boolean | null = null;
    const now = () => document.visibilityState === 'visible' && document.hasFocus();
    const tell = (force = false) => {
      const looking = now();
      if (!force && looking === told) return;
      told = looking;
      this.rt.command('presence.looking', { looking });
    };
    const change = () => tell();
    const events = ['focus', 'blur', 'pageshow', 'pagehide'] as const;
    events.forEach((e) => window.addEventListener(e, change));
    document.addEventListener('visibilitychange', change);
    const offState = this.rt.onState((s) => s === 'connected' && tell(true));
    tell(true);
    this.lookingStop = () => {
      events.forEach((e) => window.removeEventListener(e, change));
      document.removeEventListener('visibilitychange', change);
      offState();
    };
  }

  /**
   * "Idle" for the others after five minutes without any activity here (or with the app in the background);
   * back to online at the first touch, key or return. Told to the server only when it changes.
   */
  private watchIdle(): void {
    const LIMIT = 5 * 60_000;
    let idle = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tell = (next: boolean) => {
      if (next === idle) return;
      idle = next;
      this.rt.command('presence.idle', { idle });
    };
    const arm = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => tell(true), LIMIT);
    };
    const active = () => {
      if (document.visibilityState !== 'visible') return;
      tell(false);
      arm();
    };
    const visibility = () => (document.visibilityState === 'visible' ? active() : arm());
    const events = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;
    let last = 0;
    const throttled = () => {
      const now = Date.now();
      if (now - last < 15_000 && !idle) return;
      last = now;
      active();
    };
    events.forEach((e) => window.addEventListener(e, throttled, { passive: true }));
    document.addEventListener('visibilitychange', visibility);
    // A reconnect starts as online on the server: say idle again if it still is.
    const offState = this.rt.onState((s) => s === 'connected' && idle && this.rt.command('presence.idle', { idle: true }));
    arm();
    this.idleStop = () => {
      if (timer) clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, throttled));
      document.removeEventListener('visibilitychange', visibility);
      offState();
    };
  }
  private persistTimer: ReturnType<typeof setTimeout> | null = null;

  stop(): void {
    this.offWorker?.();
    this.offWorker = null;
    this.idleStop?.();
    this.lookingStop?.();
    this.persist?.();
    if (this.persistTimer) clearTimeout(this.persistTimer);
    this.stopped = true;
    if (this.syncRetry) clearTimeout(this.syncRetry);
    window.removeEventListener('online', this.onOnline);
    this.rt.stop();
    void this.e2ee?.stop();
    this.offResume();
    this.typingTimers.forEach(clearTimeout);
  }

  /** Called by the conversation view on mount (id) and unmount (null for that id). */
  setActive(id: string | null, ifCurrent?: string): void {
    if (ifCurrent === undefined || this.activeConversationId === ifCurrent) this.activeConversationId = id;
  }

  async loadConversations(): Promise<void> {
    const { conversations } = await api<{ conversations: ConversationDto[] }>('GET', '/conversations', undefined, LIVE_READ);
    const current = new Map(this.state.conversations.map((c) => [c.id, c]));
    const list = conversations.map((c) => fresherConversation(c, current.get(c.id), this.state.messages[c.id], this.myId));
    this.set({ conversations: sortConversations(list), loaded: true });
  }

  upsertConversation(c: ConversationDto) {
    const others = this.state.conversations.filter((x) => x.id !== c.id);
    this.set({ conversations: sortConversations([...others, c]) });
  }

  async refreshConversation(id: string): Promise<void> {
    try {
      const c = await api<ConversationDto>('GET', `/conversations/${id}`);
      // A chat I deleted stays out of the list (the server's "updated" note would bring it back) until a new
      // message arrives in it, as in WhatsApp.
      const removed = this.removedAt.get(id);
      if (removed !== undefined) {
        if (c.lastSeq <= removed) return;
        this.removedAt.delete(id);
      }
      this.upsertConversation(fresherConversation(c, this.state.conversations.find((x) => x.id === id), this.state.messages[id], this.myId));
      this.checkTail(c);
    } catch (err) {
      // Left the group or was removed: drop it from the list.
      if (err instanceof ApiError && err.code === 'NOT_FOUND') {
        this.set({ conversations: this.state.conversations.filter((c) => c.id !== id) });
        return;
      }
      throw err;
    }
  }

  async startDirect(userId: string): Promise<ConversationDto> {
    const c = await api<ConversationDto>('POST', '/conversations/direct', { userId });
    this.upsertConversation(c);
    return c;
  }

  /** Message fetches on their way, per chat: opening, a prefetch and a sync never ask the same twice at once. */
  private opening = new Map<string, { p: Promise<void>; since: number }>();

  /**
   * Loads the newest page the first time, afterwards only what is missing (cursor sync). `force`: a fetch under
   * way for more than a few seconds (likely stuck on a connection that died) is not joined; a new one is asked.
   */
  openConversation(id: string, opts: { force?: boolean } = {}): Promise<void> {
    const running = this.opening.get(id);
    if (running && !(opts.force && Date.now() - running.since > STALE_OPEN_MS)) return running.p;
    const p = this.fetchNewer(id).finally(() => {
      if (this.opening.get(id)?.p === p) this.opening.delete(id);
    });
    this.opening.set(id, { p, since: Date.now() });
    return p;
  }

  /**
   * A chat the person is likely to open next (pointer over its row, or near the top of the list while the app
   * is idle): its newest page is fetched quietly, so opening it shows the messages at once. Never for a chat
   * already on hand, a locked one, or more than a few at a time.
   */
  prefetch(id: string): void {
    const c = this.state.conversations.find((x) => x.id === id);
    if (!c || c.locked || this.state.messages[id] || this.opening.has(id) || this.opening.size >= 3) return;
    void this.openConversation(id).catch(() => undefined);
  }

  private async fetchNewer(id: string): Promise<void> {
    // A chat open from the start (a reload inside it, a notification) asks only for what is newer than the copy
    // on this device, once that copy has been read.
    await this.hydrated;
    const known = this.state.messages[id];
    const last = known?.at(-1)?.seq;
    const query = last === undefined ? 'limit=50' : `afterSeq=${last}&limit=200`;
    // A copy kept on this device (or an earlier version of the app) may have holes: checked once per session.
    const hole = last !== undefined && !this.holesChecked.has(id) ? firstHole(known) : null;
    this.holesChecked.add(id);
    let page: { messages: MessageDto[]; hasMore: boolean };
    try {
      page = await api<{ messages: MessageDto[]; hasMore: boolean }>('GET', `/conversations/${id}/messages?${query}`, undefined, LIVE_READ);
    } catch (err) {
      this.early.delete(id);
      throw err;
    }
    // Merged into the messages as they are now, not as they were when asked: whatever the socket brought in the
    // meantime stays (merging into the earlier copy dropped it, leaving holes until a reload).
    const early = this.early.get(id) ?? [];
    this.early.delete(id);
    const list = mergeMessages(this.state.messages[id], [...page.messages, ...early]);
    this.set({
      messages: { ...this.state.messages, [id]: list },
      ...(last === undefined ? { hasMore: { ...this.state.hasMore, [id]: page.hasMore } } : {}),
    });
    // Messages that came over the socket while the first page was on its way may lie beyond it.
    for (const m of early) this.checkGap(id, list, m.seq);
    if (hole) this.queueGap(id, hole[0], hole[1]);
    if (last !== undefined && page.hasMore) await this.fetchNewer(id);
  }

  /** Per chat: messages the socket brought while its first page was on its way (merged when it arrives). */
  private early = new Map<string, MessageDto[]>();
  /** Chats whose copy on this device was checked for holes this session. */
  private holesChecked = new Set<string>();
  /** Per chat: the range of seqs still to fetch (exclusive bounds), and the chats whose fill is under way. */
  private gaps = new Map<string, { after: number; before: number }>();
  private filling = new Set<string>();

  /**
   * A message with this seq is on hand: when the message before it is not, a hole is noted and filled from the
   * server (after a moment: a message overtaken on the way usually turns up by itself).
   */
  private checkGap(id: string, list: readonly MessageDto[], seq: number): void {
    let prev = 0;
    for (const m of list) if (m.seq < seq && m.seq > prev) prev = m.seq;
    // Nothing older on hand (a chat cleared, or this is its first message here): there is no hole to speak of.
    if (prev === 0) return;
    // Seqs the server already said are not for me do not count as missing.
    const unseen = this.unseen.get(id);
    while (prev + 1 < seq && unseen?.has(prev + 1)) prev++;
    if (seq > prev + 1) this.queueGap(id, prev, seq);
  }

  /** Per chat: seqs a fill found I cannot see (hidden for me, disappeared), so they are never asked for again. */
  private unseen = new Map<string, Set<number>>();

  /** Per chat: the server's last seq a missing end was already asked for (never twice for the same one). */
  private tailChecked = new Map<string, number>();

  /**
   * The server says the chat goes further than the messages on hand: the last ones were lost on the way (no later
   * message shows the hole). Asked for once per last seq: the newest may be one I cannot see.
   */
  private checkTail(c: ConversationDto): void {
    const tail = this.state.messages[c.id]?.at(-1)?.seq;
    if (tail === undefined || c.lastSeq <= tail || this.tailChecked.get(c.id) === c.lastSeq) return;
    this.tailChecked.set(c.id, c.lastSeq);
    this.queueGap(c.id, tail, c.lastSeq + 1);
  }

  private queueGap(id: string, after: number, before: number): void {
    const g = this.gaps.get(id);
    this.gaps.set(id, g ? { after: Math.min(g.after, after), before: Math.max(g.before, before) } : { after, before });
    void this.fillGaps(id);
  }

  /**
   * Fetches the missing seqs of a chat's holes (the history endpoint, from the seq before the hole on), one fill
   * per chat at a time and a bounded number of pages. Seqs the server does not return are messages I cannot see
   * (hidden for me, disappeared), so a hole never makes it ask again on its own. Then the chat's preview and unread
   * count are read again: the filled messages never came by the socket, so nothing counted them.
   */
  private async fillGaps(id: string): Promise<void> {
    if (this.filling.has(id)) return;
    this.filling.add(id);
    let added = false;
    try {
      await new Promise((r) => setTimeout(r, GAP_GRACE_MS));
      for (let gap = this.gaps.get(id); gap; gap = this.gaps.get(id)) {
        this.gaps.delete(id);
        let cursor = gap.after;
        for (let i = 0; i < GAP_MAX_PAGES && cursor < gap.before - 1; i++) {
          const have = new Set((this.state.messages[id] ?? []).map((m) => m.seq));
          const unseen = this.unseen.get(id);
          // Everything already arrived by itself (or is not for me): nothing to ask.
          while (cursor < gap.before - 1 && (have.has(cursor + 1) || unseen?.has(cursor + 1))) cursor++;
          if (cursor >= gap.before - 1) break;
          const page = await api<{ messages: MessageDto[]; hasMore: boolean }>('GET', `/conversations/${id}/messages?afterSeq=${cursor}&limit=200`);
          const known = this.state.messages[id];
          // The chat was cleared or left meanwhile: nothing to fill.
          if (!known) {
            this.gaps.delete(id);
            return;
          }
          const on = new Set(known.map((m) => m.id));
          if (page.messages.some((m) => !on.has(m.id))) added = true;
          const list = mergeMessages(known, page.messages);
          this.set({ messages: { ...this.state.messages, [id]: list } });
          // Every seq the server went over without returning it (and not on hand) is one I cannot see. Seqs are
          // written in order, so all below one that arrived were already there when this was asked.
          const next = page.messages.at(-1)?.seq;
          const through = Math.min(gap.before - 1, page.hasMore && next !== undefined ? next : gap.before - 1);
          if (through - cursor <= 5000) {
            const onHand = new Set(list.map((m) => m.seq));
            const missing = this.unseen.get(id) ?? new Set<number>();
            for (let s = cursor + 1; s <= through; s++) if (!onHand.has(s)) missing.add(s);
            if (missing.size) this.unseen.set(id, missing);
          }
          if (!page.hasMore || next === undefined || next <= cursor) break;
          cursor = next;
        }
      }
    } catch {
      // Offline or failing: the next sync (on reconnect or back in front) fetches what is newer, and a later
      // message, or the next session's check, finds this hole again.
      this.gaps.delete(id);
    } finally {
      this.filling.delete(id);
    }
    if (added) this.recount(id);
  }

  private recounts = new Map<string, ReturnType<typeof setTimeout>>();

  /** Reads a chat's preview and unread count from the server again, once for a burst of reasons to. */
  private recount(id: string): void {
    if (this.recounts.has(id)) return;
    this.recounts.set(
      id,
      setTimeout(() => {
        this.recounts.delete(id);
        void this.refreshConversation(id)
          .then(() => {
            if (this.activeConversationId === id) this.markRead(id);
          })
          .catch(() => undefined);
      }, GAP_GRACE_MS),
    );
  }

  async loadOlder(id: string, limit = 50): Promise<void> {
    const first = this.state.messages[id]?.[0]?.seq;
    if (!first || first <= 1) return;
    const page = await api<{ messages: MessageDto[]; hasMore: boolean }>(
      'GET',
      `/conversations/${id}/messages?beforeSeq=${first}&limit=${limit}`,
    );
    this.set({
      messages: { ...this.state.messages, [id]: mergeMessages(this.state.messages[id], page.messages) },
      hasMore: { ...this.state.hasMore, [id]: page.hasMore },
    });
  }

  /**
   * Loads older pages (in big steps) until the message with this seq is on hand: jumping to a date, a search
   * result or the first unread message far back. Bounded, so a huge chat never loads without end.
   */
  async loadUntil(id: string, seq: number, maxPages = 60): Promise<void> {
    for (let i = 0; i < maxPages; i++) {
      const first = this.state.messages[id]?.[0]?.seq;
      if (first === undefined || first <= seq || !this.state.hasMore[id]) return;
      await this.loadOlder(id, 200);
    }
  }

  /**
   * After (re)connecting: refresh summaries, fetch new messages for loaded conversations, and catch up
   * on edits, deletions and reactions to messages already on screen (server clock, so device clocks
   * don't matter).
   */
  private sync(): Promise<void> {
    // A reconnect and a return to the front often come together: one catch-up at a time, and once more after
    // it when asked meanwhile (what changed during the first one is not missed).
    if (this.syncing) {
      this.syncAgain = true;
      return this.syncing;
    }
    const run = async () => {
      do {
        this.syncAgain = false;
        await this.syncOnce();
      } while (this.syncAgain);
    };
    const p = run().finally(() => {
      if (this.syncing === p) this.syncing = null;
    });
    this.syncing = p;
    return p;
  }

  private syncing: Promise<void> | null = null;
  private syncAgain = false;
  /**
   * Chats whose catch-up failed (a flaky connection on the way back, a locked chat): where theirs starts next
   * time. The others move on to the new sync time; these must not, or what changed in them meanwhile is lost.
   */
  private behind = new Map<string, string>();

  private async syncOnce(): Promise<void> {
    // The copy on this device first: its sync time and messages say where each chat's catch-up starts.
    await this.hydrated;
    try {
      const { serverTime } = await api<{ serverTime: string }>('GET', '/sync', undefined, LIVE_READ);
      await this.loadConversations();
      // The open chat first, then the others a few at a time (not one after another). The open chat is in it even
      // with nothing on hand yet (its first fetch failed on the way in).
      const active = this.activeConversationId;
      const ids = [...new Set([...(active ? [active] : []), ...Object.keys(this.state.messages)])].sort(
        (a, b) => Number(b === this.activeConversationId) - Number(a === this.activeConversationId),
      );
      const since = this.syncedAt;
      const catchUp = async (id: string) => {
        const from = catchUpFrom(since, this.behind.get(id), this.state.messages[id]);
        try {
          // Not joined to a fetch stuck since before the reconnect (it would hold the catch-up back).
          await this.openConversation(id, { force: true });
          if (from) await this.catchUpChanges(id, from);
          this.behind.delete(id);
        } catch {
          // One chat (a locked one, say) must not stop the others from catching up; it starts from here next time.
          if (from) this.behind.set(id, from);
        }
      };
      for (let i = 0; i < ids.length; i += 4) await Promise.all(ids.slice(i, i + 4).map(catchUp));
      this.syncedAt = serverTime;
    } catch {
      // Tried again shortly while connected (a request lost on a connection that was dying), and on every reconnect.
      if (this.syncRetry) clearTimeout(this.syncRetry);
      this.syncRetry = setTimeout(() => {
        this.syncRetry = null;
        if (!this.stopped && this.rt.state === 'connected') void this.sync();
      }, SYNC_RETRY_MS);
    }
    void this.flush();
  }

  private syncRetry: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;

  private async catchUpChanges(conversationId: string, since: string): Promise<void> {
    // A minute of overlap: changes committed around the previous sync are read again; applying is idempotent.
    let from = new Date(Date.parse(since) - 60_000).toISOString();
    for (let page = 0; page < 20; page++) {
      const c = await api<MessageChangesDto>('GET', `/conversations/${conversationId}/changes?since=${encodeURIComponent(from)}`, undefined, LIVE_READ);
      for (const m of c.messages) this.applyUpdate(m);
      if (c.hiddenIds.length) {
        const hidden = new Set(c.hiddenIds);
        const known = this.state.messages[conversationId];
        if (known?.some((m) => hidden.has(m.id))) {
          this.set({ messages: { ...this.state.messages, [conversationId]: known.filter((m) => !hidden.has(m.id)) } });
        }
      }
      if (!c.hasMore) return;
      from = c.nextSince;
    }
  }

  // ---------- sending ----------

  async send(
    conversationId: string,
    body: string,
    replyToId?: string,
    linkPreviewUrl?: string,
    opts: { forwarded?: boolean; mentions?: string[]; threadRootId?: string; linkCard?: EncryptedLinkCard } = {},
  ): Promise<void> {
    await this.queueText(conversationId, body, replyToId, linkPreviewUrl, opts);
  }

  /** send(), answering the queued message's clientMsgId. */
  private async queueText(
    conversationId: string,
    body: string,
    replyToId?: string,
    linkPreviewUrl?: string,
    opts: { forwarded?: boolean; mentions?: string[]; threadRootId?: string; linkCard?: EncryptedLinkCard } = {},
  ): Promise<string> {
    await this.maybeAutoEncrypt(conversationId);
    const item: PendingMessage = {
      clientMsgId: uuidv7(),
      conversationId,
      body,
      createdAt: new Date().toISOString(),
      status: 'queued',
      ...(replyToId ? { replyToId } : {}),
      ...(linkPreviewUrl ? { linkPreviewUrl } : {}),
      ...(opts.forwarded ? { forwarded: true } : {}),
      ...(opts.mentions?.length ? { mentions: opts.mentions } : {}),
      ...(opts.threadRootId ? { threadRootId: opts.threadRootId } : {}),
      ...(opts.linkCard ? { linkCard: opts.linkCard } : {}),
    };
    this.setPending(conversationId, [...(this.state.pending[conversationId] ?? []), item]);
    await outbox.put(item);
    void this.flush();
    return item.clientMsgId;
  }

  /**
   * Uploads first (needs a connection), then queues the message that references the upload,
   * so a dropped connection after the upload still delivers it exactly once.
   */
  async sendAttachment(
    conversationId: string,
    file: Blob,
    name: string,
    opts: SendAttachmentOptions = {},
  ): Promise<void> {
    await this.queueAttachment(conversationId, file, name, opts);
  }

  /** sendAttachment(), answering the queued message's clientMsgId. */
  private async queueAttachment(conversationId: string, file: Blob, name: string, opts: SendAttachmentOptions = {}): Promise<string> {
    await this.maybeAutoEncrypt(conversationId);
    const conv = this.state.conversations.find((c) => c.id === conversationId);
    if (conv?.e2ee) return this.sendEncryptedAttachment(conversationId, file, name, opts);
    const result = await uploadAttachment(conversationId, file, name, uploadOptions(opts), { onProgress: opts.onProgress, signal: opts.signal });
    if (!('attachment' in result)) throw new ApiError('UNKNOWN', 0);
    const { attachment } = result;
    const item: PendingMessage = {
      clientMsgId: uuidv7(),
      conversationId,
      body: opts.viewOnce ? '' : (opts.caption ?? ''),
      attachment,
      ...(opts.viewOnce ? { viewOnce: true } : {}),
      ...(opts.forwarded ? { forwarded: true } : {}),
      createdAt: new Date().toISOString(),
      status: 'queued',
    };
    this.setPending(conversationId, [...(this.state.pending[conversationId] ?? []), item]);
    await outbox.put(item);
    void this.flush();
    return item.clientMsgId;
  }

  /** Encrypts on this device, uploads ciphertext only, then queues the encrypted message carrying the key. */
  private async sendEncryptedAttachment(
    conversationId: string,
    file: Blob,
    name: string,
    opts: SendAttachmentOptions,
  ): Promise<string> {
    if (!this.e2ee) throw new ApiError('E2EE_UNAVAILABLE', 0);
    // What the other side sees before downloading (cover, length, shape): made here, sent encrypted.
    const media = file.type || '';
    const preview = opts.voice || opts.sticker || opts.asDocument || opts.round ? null : media.startsWith('video/') ? await videoPreview(file) : media.startsWith('image/') ? await photoPreview(file) : null;
    const { data, info } = this.e2ee.encryptFile(new Uint8Array(await file.arrayBuffer()));
    const result = await uploadAttachment(conversationId, new Blob([data as BlobPart], { type: 'application/octet-stream' }), 'encrypted', { encrypted: true }, {
      onProgress: opts.onProgress,
      signal: opts.signal,
    });
    if (!('encrypted' in result)) throw new ApiError('UNKNOWN', 0);
    const { encrypted } = result;
    const meta: E2eeFileMeta = {
      attachmentId: encrypted.id,
      name: name.slice(0, 255),
      mime: file.type || 'application/octet-stream',
      size: file.size,
      info,
      voice: Boolean(opts.voice),
      ...(opts.durationMs ? { durationMs: Math.round(opts.durationMs) } : {}),
      ...(opts.round ? { round: true } : {}),
      ...(opts.waveform?.length ? { waveform: opts.waveform } : {}),
      ...(opts.asDocument ? { document: true } : {}),
      ...(opts.sticker ? { sticker: true } : {}),
      ...(preview ? { thumb: preview.thumb, width: preview.width, height: preview.height } : {}),
      ...(!opts.durationMs && preview?.durationMs ? { durationMs: preview.durationMs } : {}),
      // The caption travels inside the encrypted content, with the file (the server never sees it).
      ...(!opts.viewOnce && opts.caption?.trim() ? { caption: opts.caption.trim().slice(0, 4000) } : {}),
    };
    return this.queueEncryptedFile(conversationId, meta, { viewOnce: opts.viewOnce, forwarded: opts.forwarded });
  }

  /** Queues the encrypted message announcing an upload (the key and the details travel inside it). */
  private async queueEncryptedFile(conversationId: string, meta: E2eeFileMeta, opts: { viewOnce?: boolean | undefined; forwarded?: boolean | undefined }): Promise<string> {
    const item: PendingMessage = {
      clientMsgId: uuidv7(),
      conversationId,
      body: '',
      e2eeFile: meta,
      ...(opts.viewOnce ? { viewOnce: true } : {}),
      ...(opts.forwarded ? { forwarded: true } : {}),
      createdAt: new Date().toISOString(),
      status: 'queued',
    };
    this.setPending(conversationId, [...(this.state.pending[conversationId] ?? []), item]);
    await outbox.put(item);
    void this.flush();
    return item.clientMsgId;
  }

  /** Ends a forward's wait for one queued copy. */
  private settle(clientMsgId: string, o: SendSettled) {
    const done = this.settleWaiters.get(clientMsgId);
    if (!done) return;
    this.settleWaiters.delete(clientMsgId);
    done(o);
  }

  /**
   * How a queued message ended: sent, refused (and why), or still waiting (offline, or longer than `ms`): it then
   * goes by itself later, like any message written offline.
   */
  private waitSettled(clientMsgId: string, ms = 30_000): Promise<SendSettled> {
    const item = Object.values(this.state.pending).flat().find((p) => p.clientMsgId === clientMsgId);
    if (!item) return Promise.resolve({ ok: true });
    if (item.status === 'failed') return Promise.resolve({ ok: false, code: (item.error as ClientErrorCode | undefined) ?? 'UNKNOWN' });
    return new Promise((resolve) => {
      const timer = setTimeout(() => this.settle(clientMsgId, { ok: true, queued: true }), ms);
      this.settleWaiters.set(clientMsgId, (o) => {
        clearTimeout(timer);
        resolve(o);
      });
      if (!navigator.onLine) this.settle(clientMsgId, { ok: true, queued: true });
    });
  }

  /** Who an encrypted message goes to: the peer, or the current members of a group. */
  private async recipients(c: ConversationDto): Promise<string[]> {
    if (c.type === 'direct') return c.peer ? [c.peer.id] : [];
    const cached = this.groupMembers.get(c.id);
    if (cached) return cached;
    const { members } = await api<{ members: { id: string }[] }>('GET', `/groups/${c.id}/members`);
    const ids = members.map((m) => m.id);
    this.groupMembers.set(c.id, ids);
    return ids;
  }

  private setPending(conversationId: string, list: PendingMessage[]) {
    this.set({ pending: { ...this.state.pending, [conversationId]: list } });
  }

  private updatePending(m: QueuedMessage, patch: Partial<PendingMessage> | null) {
    const list = this.state.pending[m.conversationId] ?? [];
    this.setPending(
      m.conversationId,
      patch === null ? list.filter((p) => p.clientMsgId !== m.clientMsgId) : list.map((p) => (p.clientMsgId === m.clientMsgId ? { ...p, ...patch } : p)),
    );
  }

  /** Sends queued messages in order. Network failures keep them queued; refusals mark them failed. */
  async flush(): Promise<void> {
    // Not before the server's list has been asked for: whether a chat is encrypted comes with it.
    await this.listed;
    if (!navigator.onLine) return;
    if (this.flushing) {
      this.flushAgain = true;
      return;
    }
    this.flushing = true;
    this.flushAgain = false;
    let rateLimited = false;
    try {
      const queue = Object.values(this.state.pending).flat().filter((p) => p.status === 'queued');
      queue.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      // Order is kept within a chat: when one message has to wait, later ones of that chat wait too, but
      // other chats go on (one stuck chat must never hold back everything else).
      const held = new Set<string>();
      for (const item of queue) {
        if (held.has(item.conversationId)) continue;
        const conv = this.state.conversations.find((c) => c.id === item.conversationId);
        // Encrypted chats wait until this device's crypto is ready; nothing is sent in plaintext. While another tab
        // of this device runs the crypto, that tab sends them (they wait here, without holding up other chats).
        if (conv?.e2ee && this.e2ee && !this.e2ee.active) {
          held.add(item.conversationId);
          continue;
        }
        if (conv?.e2ee && !this.e2ee) {
          if (this.e2eeGaveUp) {
            await outbox.remove(item.clientMsgId);
            this.updatePending(item, { status: 'failed', error: 'E2EE_UNAVAILABLE' });
            this.settle(item.clientMsgId, { ok: false, code: 'E2EE_UNAVAILABLE' });
          } else held.add(item.conversationId);
          continue;
        }
        this.updatePending(item, { status: 'sending' });
        let encrypted: Awaited<ReturnType<E2ee['encryptContent']>> | undefined;
        let previews: { pushPreviews?: { deviceId: string; ciphertext: string }[] } = {};
        try {
          const content: DecryptedContent = item.e2eeFile ? E2ee.fileContent(item.e2eeFile) : { msgtype: 'm.text', body: item.body, ...(item.linkCard ? { link_preview: item.linkCard } : {}) };
          encrypted = conv?.e2ee && this.e2ee ? await this.e2ee.encryptContent(conv.id, await this.recipients(conv), content) : undefined;
          if (encrypted && conv) previews = await this.pushPreviewsFor(conv, item.clientMsgId, content, { reply: Boolean(item.replyToId), viewOnce: Boolean(item.viewOnce) });
        } catch (err) {
          // A network hiccup while fetching keys waits; a crypto failure is shown on the message with "Retry".
          if (err instanceof ApiError && (err.code === 'NETWORK' || err.code === 'RATE_LIMITED')) {
            this.updatePending(item, { status: 'queued' });
            held.add(item.conversationId);
            this.settle(item.clientMsgId, { ok: true, queued: true });
            continue;
          }
          console.error('encrypting a message failed', err);
          await outbox.remove(item.clientMsgId);
          // Most often: this device has not been confirmed yet. Say so, so the message offers the fix.
          const code = err instanceof ApiError ? err.code : this.e2ee && !this.e2ee.deviceVerified ? 'E2EE_DEVICE_UNVERIFIED' : 'E2EE_SEND_FAILED';
          this.updatePending(item, { status: 'failed', error: code });
          this.settle(item.clientMsgId, { ok: false, code });
          continue;
        }
        try {
          const message = await api<MessageDto>('POST', `/conversations/${item.conversationId}/messages`, {
            clientMsgId: item.clientMsgId,
            body: encrypted ? '' : item.body,
            ...(encrypted ? { encrypted } : {}),
            ...(item.attachment ? { attachmentId: item.attachment.id } : {}),
            ...(item.e2eeFile ? { attachmentId: item.e2eeFile.attachmentId } : {}),
            ...(item.replyToId ? { replyToId: item.replyToId } : {}),
            ...(item.linkPreviewUrl && !encrypted ? { linkPreviewUrl: item.linkPreviewUrl } : {}),
            ...(item.forwarded ? { forwarded: true } : {}),
            ...(encrypted && item.mentions?.length ? { mentions: item.mentions } : {}),
            ...(item.viewOnce ? { viewOnce: true } : {}),
            ...(item.threadRootId ? { threadRootId: item.threadRootId } : {}),
            ...previews,
            clientSentAt: item.createdAt,
          });
          await outbox.remove(item.clientMsgId);
          this.updatePending(item, null);
          this.applyMessage(message);
          this.settle(item.clientMsgId, { ok: true });
        } catch (err) {
          const code = err instanceof ApiError ? err.code : 'UNKNOWN';
          if (code === 'NETWORK' || code === 'RATE_LIMITED' || code === 'UNKNOWN') {
            this.updatePending(item, { status: 'queued' });
            // Keep this chat's order; retried on reconnect / next send. A server outage holds every chat.
            held.add(item.conversationId);
            this.settle(item.clientMsgId, { ok: true, queued: true });
            if (code === 'RATE_LIMITED') rateLimited = true;
            if (code === 'NETWORK') break;
            continue;
          }
          await outbox.remove(item.clientMsgId);
          this.updatePending(item, { status: 'failed', error: code });
          this.settle(item.clientMsgId, { ok: false, code });
        }
      }
    } finally {
      this.flushing = false;
    }
    if (rateLimited && !this.rateRetry) {
      this.rateRetry = setTimeout(() => {
        this.rateRetry = null;
        void this.flush();
      }, 20_000);
    }
    if (this.flushAgain && !rateLimited) void this.flush();
  }

  async retry(m: PendingMessage): Promise<void> {
    const item: PendingMessage = { ...m, status: 'queued' };
    delete item.error;
    this.updatePending(m, { status: 'queued', error: undefined });
    await outbox.put({
      clientMsgId: item.clientMsgId,
      conversationId: item.conversationId,
      body: item.body,
      createdAt: item.createdAt,
      ...(item.attachment ? { attachment: item.attachment } : {}),
      ...(item.replyToId ? { replyToId: item.replyToId } : {}),
      ...(item.linkPreviewUrl ? { linkPreviewUrl: item.linkPreviewUrl } : {}),
      ...(item.e2eeFile ? { e2eeFile: item.e2eeFile } : {}),
      ...(item.mentions ? { mentions: item.mentions } : {}),
      ...(item.viewOnce ? { viewOnce: true } : {}),
      ...(item.forwarded ? { forwarded: true } : {}),
      ...(item.threadRootId ? { threadRootId: item.threadRootId } : {}),
      ...(item.linkCard ? { linkCard: item.linkCard } : {}),
    });
    // "Retry" on an encrypted message also gives the device's encryption another chance to start.
    if (!this.e2ee && this.e2eeGaveUp) {
      this.e2eeGaveUp = false;
      void this.startE2ee();
    }
    void this.flush();
  }

  discard(m: PendingMessage): void {
    void outbox.remove(m.clientMsgId);
    this.updatePending(m, null);
  }

  async loadPresence(userIds: string[]): Promise<void> {
    if (userIds.length === 0) return;
    const { presence } = await api<{ presence: PresenceDto[] }>('GET', `/presence?userIds=${userIds.join(',')}`);
    const next = { ...this.state.presence };
    for (const id of userIds) delete next[id];
    for (const p of presence) next[p.userId] = p;
    this.set({ presence: next });
  }

  // ---------- edit & delete ----------

  // ---------- location ----------

  /** Live locations this device is sharing: message id → watch. Survive a reload (kept in localStorage). */
  private liveShares = new Map<string, { conversationId: string; until: number; watch: number; last: LocationPoint | null; sentAt: number }>();
  private static LIVE_KEY = 'da.liveShares';

  private saveLiveShares() {
    try {
      localStorage.setItem(
        ChatStore.LIVE_KEY,
        JSON.stringify([...this.liveShares].map(([id, s]) => ({ id, conversationId: s.conversationId, until: s.until }))),
      );
    } catch {
      /* storage unavailable: sharing lasts while this page is open */
    }
  }

  private locationContent(p: LocationPoint) {
    return { msgtype: 'm.location', body: '📍', geo_uri: `geo:${p.lat.toFixed(6)},${p.lng.toFixed(6)}${p.accuracy !== null ? `;u=${Math.round(p.accuracy)}` : ''}` };
  }

  /** Shares a person as a contact card (inside the ciphertext in encrypted chats). */
  async sendContact(conversationId: string, card: ContactCardDto, opts: { forwarded?: boolean } = {}): Promise<void> {
    await this.maybeAutoEncrypt(conversationId);
    const conv = this.state.conversations.find((c) => c.id === conversationId);
    let payload: Record<string, unknown> = { body: '', contactUserId: card.id };
    const clientMsgId = uuidv7();
    if (conv?.e2ee) {
      if (!this.e2ee) throw new ApiError('E2EE_UNAVAILABLE', 0);
      const content = { msgtype: CONTACT_MSGTYPE, body: card.displayName, contact: card };
      payload = { body: '', encrypted: await this.e2ee.encryptContent(conv.id, await this.recipients(conv), content as never) };
      Object.assign(payload, await this.pushPreviewsFor(conv, clientMsgId, content as DecryptedContent));
    }
    this.applyMessage(await api<MessageDto>('POST', `/conversations/${conversationId}/messages`, { clientMsgId, ...payload, ...(opts.forwarded ? { forwarded: true } : {}) }));
  }

  /** Where I am, once; or live for some minutes (this device keeps the message moving). */
  async sendLocation(conversationId: string, point: LocationPoint, liveMinutes?: number, opts: { forwarded?: boolean } = {}): Promise<void> {
    await this.maybeAutoEncrypt(conversationId);
    const conv = this.state.conversations.find((c) => c.id === conversationId);
    let payload: Record<string, unknown> = { location: point };
    const clientMsgId = uuidv7();
    if (conv?.e2ee) {
      if (!this.e2ee) throw new ApiError('E2EE_UNAVAILABLE', 0);
      const content = this.locationContent(point);
      const encrypted = await this.e2ee.encryptContent(conv.id, await this.recipients(conv), content as never);
      payload = { body: '', encrypted, ...(await this.pushPreviewsFor(conv, clientMsgId, content)) };
    }
    const message = await api<MessageDto>('POST', `/conversations/${conversationId}/messages`, {
      clientMsgId,
      ...payload,
      ...(liveMinutes ? { liveMinutes } : {}),
      ...(opts.forwarded ? { forwarded: true } : {}),
    });
    this.applyMessage(message);
    if (message.liveUntil) this.trackLive(conversationId, message.id, Date.parse(message.liveUntil), point);
  }

  private trackLive(conversationId: string, messageId: string, until: number, last: LocationPoint | null) {
    if (this.liveShares.has(messageId) || until <= Date.now() || !navigator.geolocation) return;
    const watch = navigator.geolocation.watchPosition(
      (pos) => void this.moveLive(messageId, { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: Math.round(pos.coords.accuracy) }),
      () => undefined,
      { enableHighAccuracy: true, maximumAge: 15_000 },
    );
    this.liveShares.set(messageId, { conversationId, until, watch, last, sentAt: Date.now() });
    this.saveLiveShares();
    setTimeout(() => this.endLive(messageId), until - Date.now() + 1000);
  }

  /** Sends a new point when it moved enough (~15 m) or a while passed, never more than every 15 s. */
  private async moveLive(messageId: string, p: LocationPoint) {
    const share = this.liveShares.get(messageId);
    if (!share) return;
    if (Date.now() >= share.until) return this.endLive(messageId);
    const since = Date.now() - share.sentAt;
    const moved = share.last ? Math.hypot((p.lat - share.last.lat) * 111_000, (p.lng - share.last.lng) * 111_000 * Math.cos((p.lat * Math.PI) / 180)) : Infinity;
    if (since < 15_000 || (moved < 15 && since < 120_000)) return;
    share.sentAt = Date.now();
    share.last = p;
    const conv = this.state.conversations.find((c) => c.id === share.conversationId);
    try {
      const updated = conv?.e2ee && this.e2ee
        ? await api<MessageDto>('PATCH', `/conversations/${share.conversationId}/messages/${messageId}`, {
            encrypted: await this.e2ee.encryptContent(conv.id, await this.recipients(conv), this.locationContent(p) as never),
          })
        : await api<MessageDto>('PUT', `/conversations/${share.conversationId}/messages/${messageId}/location`, p);
      this.applyUpdate(updated);
    } catch (err) {
      // The share ended elsewhere (stopped on another device, or the time is up).
      if (err instanceof ApiError && err.code !== 'NETWORK') this.endLive(messageId);
    }
  }

  private endLive(messageId: string) {
    const share = this.liveShares.get(messageId);
    if (!share) return;
    navigator.geolocation?.clearWatch(share.watch);
    this.liveShares.delete(messageId);
    this.saveLiveShares();
  }

  async stopLiveLocation(conversationId: string, messageId: string): Promise<void> {
    this.endLive(messageId);
    this.applyUpdate(await api<MessageDto>('POST', `/conversations/${conversationId}/messages/${messageId}/location/stop`));
  }

  /** After a reload: keep sharing what was still live. */
  private resumeLiveShares() {
    try {
      const saved = JSON.parse(localStorage.getItem(ChatStore.LIVE_KEY) ?? '[]') as { id: string; conversationId: string; until: number }[];
      for (const s of saved) this.trackLive(s.conversationId, s.id, s.until, null);
    } catch {
      /* nothing saved */
    }
  }

  /** A sticker from the built-in pack; in encrypted chats the id travels inside the ciphertext. */
  async sendSticker(conversationId: string, sticker: string, opts: { forwarded?: boolean } = {}): Promise<void> {
    await this.maybeAutoEncrypt(conversationId);
    const conv = this.state.conversations.find((c) => c.id === conversationId);
    let payload: Record<string, unknown> = { sticker };
    const clientMsgId = uuidv7();
    if (conv?.e2ee) {
      if (!this.e2ee) throw new ApiError('E2EE_UNAVAILABLE', 0);
      const content: DecryptedContent = { msgtype: 'm.sticker', body: sticker };
      const encrypted = await this.e2ee.encryptContent(conv.id, await this.recipients(conv), content);
      payload = { body: '', encrypted, ...(await this.pushPreviewsFor(conv, clientMsgId, content)) };
    }
    this.applyMessage(await api<MessageDto>('POST', `/conversations/${conversationId}/messages`, { clientMsgId, ...payload, ...(opts.forwarded ? { forwarded: true } : {}) }));
  }

  // ---------- polls, disappearing and scheduled messages ----------

  async createPoll(conversationId: string, input: Omit<CreatePollInput, 'clientMsgId'>): Promise<void> {
    const conv = this.state.conversations.find((c) => c.id === conversationId);
    if (conv?.e2ee) {
      // Encrypted chats: the question and choices travel inside the ciphertext; votes are encrypted per person.
      const poll = { question: input.question, options: input.options, multiple: Boolean(input.multiple), anonymous: Boolean(input.anonymous) };
      return this.sendEncryptedContent(conv, { msgtype: POLL_MSGTYPE, body: input.question, poll }, input.forwarded);
    }
    this.applyMessage(await api<MessageDto>('POST', `/conversations/${conversationId}/polls`, { ...input, clientMsgId: uuidv7() }));
  }

  /** A poll or an event whose details are inside the ciphertext. */
  private async sendEncryptedContent(conv: ConversationDto, content: DecryptedContent, forwarded?: boolean): Promise<void> {
    if (!this.e2ee) throw new ApiError('E2EE_UNAVAILABLE', 0);
    const encrypted = await this.e2ee.encryptContent(conv.id, await this.recipients(conv), content);
    const clientMsgId = uuidv7();
    const previews = await this.pushPreviewsFor(conv, clientMsgId, content);
    this.applyMessage(await api<MessageDto>('POST', `/conversations/${conv.id}/messages`, { clientMsgId, body: '', encrypted, ...previews, ...(forwarded ? { forwarded: true } : {}) }));
  }

  /** Encrypted chats: my vote / event answer (and, for the creator, "closed"), encrypted for everyone. */
  async answerEncrypted(conversationId: string, messageId: string, answer: EncryptedAnswer | null): Promise<void> {
    const conv = this.state.conversations.find((c) => c.id === conversationId);
    if (!conv || !this.e2ee) throw new ApiError('E2EE_UNAVAILABLE', 0);
    const encrypted = answer === null ? null : await this.e2ee.encryptAnswer(conv.id, await this.recipients(conv), messageId, answer);
    this.applyUpdate(await api<MessageDto>('PUT', `/conversations/${conversationId}/messages/${messageId}/encrypted-answer`, { encrypted }));
  }

  async createEvent(conversationId: string, input: Omit<CreateChatEventInput, 'clientMsgId'>): Promise<void> {
    const conv = this.state.conversations.find((c) => c.id === conversationId);
    if (conv?.e2ee) {
      const event = { title: input.title, startsAt: input.startsAt, endsAt: input.endsAt ?? null, location: input.location ?? null, description: input.description ?? null };
      return this.sendEncryptedContent(conv, { msgtype: EVENT_MSGTYPE, body: input.title, event }, input.forwarded);
    }
    this.applyMessage(await api<MessageDto>('POST', `/conversations/${conversationId}/events`, { ...input, clientMsgId: uuidv7() }));
  }

  async answerEvent(conversationId: string, messageId: string, answer: RsvpAnswer | null): Promise<void> {
    this.applyUpdate(await api<MessageDto>('PUT', `/conversations/${conversationId}/messages/${messageId}/rsvp`, { answer }));
  }

  async cancelEvent(conversationId: string, messageId: string): Promise<void> {
    this.applyUpdate(await api<MessageDto>('POST', `/conversations/${conversationId}/messages/${messageId}/cancel-event`));
  }

  async votePoll(conversationId: string, messageId: string, optionIds: number[]): Promise<void> {
    this.applyUpdate(await api<MessageDto>('PUT', `/conversations/${conversationId}/messages/${messageId}/vote`, { optionIds }));
  }

  async closePoll(conversationId: string, messageId: string): Promise<void> {
    this.applyUpdate(await api<MessageDto>('POST', `/conversations/${conversationId}/messages/${messageId}/close-poll`));
  }

  async setTimer(conversationId: string, seconds: number | null): Promise<void> {
    await api('PUT', `/conversations/${conversationId}/timer`, { seconds });
    await this.refreshConversation(conversationId);
  }

  /** Text for a scheduled message: encrypted here in encrypted chats (the server never sees it). */
  private async scheduledPayload(conversationId: string, body: string, mentions?: string[]) {
    await this.maybeAutoEncrypt(conversationId);
    const conv = this.state.conversations.find((c) => c.id === conversationId);
    if (!conv?.e2ee) return { body };
    if (!this.e2ee) throw new ApiError('E2EE_UNAVAILABLE', 0);
    const encrypted = await this.e2ee.encryptContent(conv.id, await this.recipients(conv), { msgtype: 'm.text', body });
    return { body: '', encrypted, ...(mentions?.length ? { mentions } : {}) };
  }

  async schedule(conversationId: string, body: string, sendAt: Date, opts: { replyToId?: string; mentions?: string[] } = {}): Promise<ScheduledMessageDto> {
    const payload = await this.scheduledPayload(conversationId, body, opts.mentions);
    return api<ScheduledMessageDto>('POST', `/conversations/${conversationId}/scheduled`, {
      clientMsgId: uuidv7(),
      ...payload,
      ...(opts.replyToId ? { replyToId: opts.replyToId } : {}),
      sendAt: sendAt.toISOString(),
    });
  }

  async updateScheduled(s: ScheduledMessageDto, patch: { body?: string; sendAt?: Date }): Promise<void> {
    const text = patch.body === undefined ? {} : await this.scheduledPayload(s.conversationId, patch.body);
    const { body, encrypted } = text as { body?: string; encrypted?: unknown };
    await api('PATCH', `/scheduled/${s.id}`, {
      ...(encrypted ? { encrypted } : body !== undefined ? { body } : {}),
      ...(patch.sendAt ? { sendAt: patch.sendAt.toISOString() } : {}),
    });
  }

  async editMessage(conversationId: string, messageId: string, body: string): Promise<void> {
    const conv = this.state.conversations.find((c) => c.id === conversationId);
    // Encrypted chats: the new text is encrypted here; the server only ever gets ciphertext.
    const payload =
      conv?.e2ee && this.e2ee
        ? { encrypted: await this.e2ee.encryptContent(conv.id, await this.recipients(conv), { msgtype: 'm.text', body }) }
        : { body };
    this.applyUpdate(await api<MessageDto>('PATCH', `/conversations/${conversationId}/messages/${messageId}`, payload));
  }

  async deleteMessage(conversationId: string, messageId: string, scope: 'everyone' | 'me'): Promise<void> {
    const result = await api<MessageDto | undefined>('DELETE', `/conversations/${conversationId}/messages/${messageId}?scope=${scope}`);
    if (result) this.applyUpdate(result);
    else {
      // Hidden for me only: drop it locally and refresh the list preview.
      const list = (this.state.messages[conversationId] ?? []).filter((m) => m.id !== messageId);
      this.set({ messages: { ...this.state.messages, [conversationId]: list } });
      void this.refreshConversation(conversationId).catch(() => undefined);
    }
  }

  // ---------- reactions, pins, forwarding ----------

  /** `emoji = null` removes the caller's reaction. */
  async react(conversationId: string, messageId: string, emoji: string | null): Promise<void> {
    const conv = this.state.conversations.find((c) => c.id === conversationId);
    if (conv?.e2ee) {
      // Encrypted chats: the emoji is encrypted here for everyone in the chat; the server only stores that I reacted.
      if (!this.e2ee) throw new ApiError('E2EE_UNAVAILABLE', 0);
      const encrypted = emoji === null ? null : await this.e2ee.encryptReaction(conv.id, await this.recipients(conv), messageId, emoji);
      this.applyUpdate(await api<MessageDto>('PUT', `/conversations/${conversationId}/messages/${messageId}/encrypted-reaction`, { encrypted }));
      return;
    }
    const path = `/conversations/${conversationId}/messages/${messageId}/reaction`;
    // Channels: the new counts and my choice show at once (as in WhatsApp); the server's answer settles them,
    // and a refusal puts the post back as it was.
    const before = conv?.type === 'channel' ? this.state.messages[conversationId]?.find((x) => x.id === messageId) : undefined;
    if (before) this.applyUpdate({ ...before, reactions: adjustCounts(before.reactions, before.myReaction, emoji), myReaction: emoji });
    try {
      this.applyUpdate(await (emoji === null ? api<MessageDto>('DELETE', path) : api<MessageDto>('PUT', path, { emoji })));
    } catch (err) {
      if (before) this.applyUpdate({ ...before, myReaction: before.myReaction ?? null });
      throw err;
    }
  }

  async loadFolders(): Promise<void> {
    const { folders } = await api<{ folders: ChatFolderDto[] }>('GET', '/chat-folders');
    this.set({ folders });
  }

  async saveFolder(input: ChatFolderInput, id: string | null): Promise<ChatFolderDto> {
    const folder = await api<ChatFolderDto>(id ? 'PUT' : 'POST', id ? `/chat-folders/${id}` : '/chat-folders', input);
    const others = this.state.folders.filter((f) => f.id !== folder.id);
    this.set({ folders: id ? this.state.folders.map((f) => (f.id === id ? folder : f)) : [...others, folder] });
    return folder;
  }

  async deleteFolder(id: string): Promise<void> {
    await api('DELETE', `/chat-folders/${id}`);
    this.set({ folders: this.state.folders.filter((f) => f.id !== id) });
  }

  async orderFolders(ids: string[]): Promise<void> {
    const byId = new Map(this.state.folders.map((f) => [f.id, f]));
    this.set({ folders: ids.map((id) => byId.get(id)).filter((f): f is ChatFolderDto => Boolean(f)) });
    const { folders } = await api<{ folders: ChatFolderDto[] }>('PUT', '/chat-folders/order', { ids });
    this.set({ folders });
  }

  /** "Clear chat" empties my copy; `remove` also takes it off my list until a new message (for me only). */
  async clearChat(conversationId: string, remove: boolean): Promise<void> {
    await api('POST', `/conversations/${conversationId}/clear`, { remove });
    if (remove) this.removedAt.set(conversationId, this.state.conversations.find((c) => c.id === conversationId)?.lastSeq ?? 0);
    this.set({
      messages: { ...this.state.messages, [conversationId]: [] },
      hasMore: { ...this.state.hasMore, [conversationId]: false },
      conversations: remove ? this.state.conversations.filter((c) => c.id !== conversationId) : this.state.conversations,
    });
    if (!remove) await this.refreshConversation(conversationId);
  }

  /** Leaves a group or channel; it disappears from my list. */
  async leaveGroup(conversationId: string): Promise<void> {
    await api('DELETE', `/groups/${conversationId}/members/me`);
    this.set({ conversations: this.state.conversations.filter((c) => c.id !== conversationId) });
  }

  /** Deletes a group or channel for everyone (its owner, or staff); it leaves every member's list. */
  async deleteGroup(conversationId: string): Promise<void> {
    await api('DELETE', `/groups/${conversationId}`);
    this.set({ conversations: this.state.conversations.filter((c) => c.id !== conversationId) });
  }

  /** Pin, archive or mark unread (my own list only). */
  async setChatState(conversationId: string, patch: { pinned?: boolean; archived?: boolean; unread?: boolean; notifyLevel?: NotifyLevel }): Promise<void> {
    this.upsertConversation(await api<ConversationDto>('PATCH', `/conversations/${conversationId}/state`, patch));
  }

  async loadStarred(): Promise<StarredMessageDto[]> {
    const { starred } = await api<{ starred: StarredMessageDto[] }>('GET', '/starred');
    this.set({ starred: new Set(starred.map((s) => s.message.id)) });
    return starred;
  }

  async loadReminders(): Promise<ReminderDto[]> {
    const { reminders } = await api<{ reminders: ReminderDto[] }>('GET', '/reminders');
    this.set({ reminders: new Map(reminders.map((r) => [r.messageId, r.remindAt])) });
    return reminders;
  }

  /** "Bana hatırlat": set or move (a date) or remove (null) the reminder on a message. */
  async setReminder(conversationId: string, messageId: string, at: Date | null): Promise<void> {
    if (at) await api('PUT', `/conversations/${conversationId}/messages/${messageId}/reminder`, { at: at.toISOString() });
    else await api('DELETE', `/conversations/${conversationId}/messages/${messageId}/reminder`);
    const next = new Map(this.state.reminders);
    if (at) next.set(messageId, at.toISOString());
    else next.delete(messageId);
    this.set({ reminders: next });
  }

  async star(conversationId: string, messageId: string, starred: boolean): Promise<void> {
    await api(starred ? 'PUT' : 'DELETE', `/conversations/${conversationId}/messages/${messageId}/star`);
    const next = new Set(this.state.starred);
    if (starred) next.add(messageId);
    else next.delete(messageId);
    this.set({ starred: next });
  }

  async pin(conversationId: string, messageId: string, pinned: boolean): Promise<void> {
    await api(pinned ? 'PUT' : 'DELETE', `/conversations/${conversationId}/pins/${messageId}`);
    await this.refreshConversation(conversationId);
  }

  /**
   * Forwards one message to each target (lib/forwarding.ts): plaintext into plaintext chats is copied by the
   * server; whenever the message or the target is end-to-end encrypted this device does it, reading the message
   * (decrypting it) and sending it again through the ordinary send paths, which encrypt per target. One answer
   * per target, in their order: sent, waiting for the network (it then goes by itself), or why not.
   */
  async forward(source: MessageDto, targets: string[], opts: { onProgress?: (conversationId: string, share: number) => void } = {}): Promise<ForwardOutcome[]> {
    const ids = [...new Set(targets)];
    const out = new Map<string, ForwardOutcome>();
    const byServer: string[] = [];
    const byDevice: string[] = [];
    for (const id of ids) {
      // A direct chat may switch to encryption on its first message (automatic encryption): decided before routing.
      await this.maybeAutoEncrypt(id).catch(() => undefined);
      const route = forwardRoute(source, { e2ee: this.isE2ee(id) });
      if (route === 'never') out.set(id, { conversationId: id, ok: false, code: 'NOT_FORWARDABLE' });
      else (route === 'server' ? byServer : byDevice).push(id);
    }
    if (byServer.length) {
      try {
        const result = await api<{ forwarded: MessageDto[]; failed: string[]; reasons?: Record<string, ClientErrorCode> }>(
          'POST',
          `/conversations/${source.conversationId}/messages/${source.id}/forward`,
          { conversationIds: byServer },
        );
        for (const m of result.forwarded) this.applyMessage(m);
        for (const id of byServer) {
          out.set(id, result.failed.includes(id) ? { conversationId: id, ok: false, code: result.reasons?.[id] ?? 'UNKNOWN' } : { conversationId: id, ok: true });
        }
      } catch (err) {
        for (const id of byServer) out.set(id, { conversationId: id, ok: false, code: failureOf(err) });
      }
    }
    if (byDevice.length) {
      const encrypted = source.contentType === 'encrypted';
      const plain = encrypted && this.e2ee ? await this.e2ee.decrypt(source).catch(() => null) : null;
      const item = forwardItem(source, plain?.content ?? null);
      const why: ForwardFailure | null = encrypted && !this.e2ee ? 'E2EE_UNAVAILABLE' : encrypted && !plain ? 'UNREADABLE' : !item ? 'NOT_FORWARDABLE' : null;
      const copy = item && !why ? this.forwardCopier(item) : null;
      for (const id of byDevice) {
        if (!copy) {
          out.set(id, { conversationId: id, ok: false, code: why ?? 'NOT_FORWARDABLE' });
          continue;
        }
        // Encrypted chats need this device's encryption: said at once instead of waiting in the queue.
        if (this.isE2ee(id) && !this.e2ee) {
          out.set(id, { conversationId: id, ok: false, code: 'E2EE_UNAVAILABLE' });
          continue;
        }
        try {
          out.set(id, { conversationId: id, ...(await copy(id, (share) => opts.onProgress?.(id, share))) });
        } catch (err) {
          if (!(err instanceof ApiError)) console.error('forwarding failed', err);
          out.set(id, { conversationId: id, ok: false, code: failureOf(err) });
        }
      }
    }
    return ids.map((id) => out.get(id) ?? { conversationId: id, ok: false, code: 'UNKNOWN' });
  }

  /**
   * "Send to a chat" from a Market app or a Meydan post: the same as writing it there (encrypted in encrypted
   * chats), answering how it ended (sent, waiting for the network, or why not) instead of only queueing it.
   */
  async sendToChat(conversationId: string, payload: ShareToChat, onProgress?: (share: number) => void): Promise<SendSettled> {
    try {
      const caption = payload.caption?.trim() ?? '';
      if ('text' in payload) return await this.waitSettled(await this.queueText(conversationId, caption ? `${payload.text}\n\n${caption}` : payload.text));
      if ('location' in payload) {
        await this.sendLocation(conversationId, payload.location);
        return caption ? await this.waitSettled(await this.queueText(conversationId, caption)) : { ok: true };
      }
      return await this.waitSettled(
        await this.queueAttachment(conversationId, payload.file, payload.name, {
          ...(caption ? { caption } : {}),
          onProgress: (sent, total) => onProgress?.(total ? sent / total : 1),
        }),
      );
    } catch (err) {
      if (!(err instanceof ApiError)) console.error('sending to a chat failed', err);
      return { ok: false, code: failureOf(err) };
    }
  }

  private isE2ee(conversationId: string): boolean {
    return Boolean(this.state.conversations.find((c) => c.id === conversationId)?.e2ee);
  }

  /** Sends one forwarded copy of `item` into a target, through the same paths as writing it there. */
  private forwardCopier(item: ForwardItem): (target: string, progress: (share: number) => void) => Promise<SendSettled> {
    const fwd = { forwarded: true };
    switch (item.kind) {
      case 'text': {
        // A plaintext message's server preview becomes the card an encrypted chat carries (made once).
        let card: Promise<EncryptedLinkCard | null> | null = null;
        return async (target) => {
          const e2ee = this.isE2ee(target);
          let linkCard = item.linkCard;
          if (e2ee && !linkCard && item.linkPreview) linkCard = await (card ??= cardFromPreview(item.linkPreview).catch(() => null));
          // Into a plaintext chat the server shows its own preview of the same link (when it has read it).
          const url = item.linkPreview?.url ?? item.linkCard?.url;
          const previewUrl = !e2ee && url && item.body.includes(url) ? url : undefined;
          return this.waitSettled(await this.queueText(target, item.body, undefined, previewUrl, { ...fwd, ...(e2ee && linkCard ? { linkCard } : {}) }));
        };
      }
      case 'sticker':
        return async (target) => (await this.sendSticker(target, item.sticker, fwd), { ok: true });
      case 'location':
        return async (target) => (await this.sendLocation(target, item.point, undefined, fwd), { ok: true });
      case 'contact':
        return async (target) => (await this.sendContact(target, item.card, fwd), { ok: true });
      case 'poll':
        // A new poll with the same question and choices; the votes stay with the chat it was asked in.
        return async (target) => (await this.createPoll(target, { ...item.poll, ...fwd }), { ok: true });
      case 'event':
        return async (target) => (await this.createEvent(target, { ...item.event, ...fwd }), { ok: true });
      case 'file': {
        const { meta, source } = item;
        // The file itself, fetched (and decrypted) once for every target that needs it, and let go afterwards.
        let blob: Promise<Blob> | null = null;
        return async (target, progress) => {
          const e2ee = this.isE2ee(target);
          // Encrypted into encrypted: the server copies the ciphertext it holds, the same key goes in the new message.
          if (e2ee && source.encrypted) return this.waitSettled(await this.forwardEncryptedCopy(target, source, meta, item.caption));
          const file = await (blob ??= this.forwardBlob(source, meta.mime, (share) => progress(share / 2)));
          const timed = Boolean(meta.durationMs);
          const id = await this.queueAttachment(target, file, meta.name, {
            ...fwd,
            ...(item.caption.trim() ? { caption: item.caption } : {}),
            // A voice note or a round video stays one (they need their length); a photo sent as a file stays a file.
            ...(meta.voice && timed ? { voice: true } : {}),
            ...(meta.round && timed ? { round: true } : {}),
            ...(meta.durationMs ? { durationMs: meta.durationMs } : {}),
            ...(meta.waveform?.length ? { waveform: meta.waveform } : {}),
            ...(meta.document && meta.mime.startsWith('image/') ? { asDocument: true } : {}),
            ...(meta.sticker ? { sticker: true } : {}),
            onProgress: (sent, total) => progress(0.5 + (total ? sent / total : 1) / 2),
          });
          progress(1);
          return this.waitSettled(id);
        };
      }
    }
  }

  /** The forwarded file's bytes on this device: downloaded, and decrypted when it came from an encrypted chat. */
  private async forwardBlob(source: ForwardFileSource, mime: string, progress: (share: number) => void): Promise<Blob> {
    const bytes = await fetchBytes(`/api/v1/attachments/${source.attachmentId}`, progress);
    if (!source.encrypted) return new Blob([bytes as BlobPart], { type: mime });
    if (!this.e2ee) throw new ApiError('E2EE_UNAVAILABLE', 0);
    return new Blob([this.e2ee.decryptFile(bytes, source.info) as BlobPart], { type: mime });
  }

  /** An encrypted file into another encrypted chat, without downloading it: a server-side copy of the ciphertext. */
  private async forwardEncryptedCopy(target: string, source: Extract<ForwardFileSource, { encrypted: true }>, m: ForwardFileMeta, caption: string): Promise<string> {
    const { encrypted } = await api<{ encrypted: { id: string; size: number } }>('POST', `/attachments/${source.attachmentId}/copy`, { conversationId: target });
    const meta: E2eeFileMeta = {
      attachmentId: encrypted.id,
      name: m.name.slice(0, 255),
      mime: m.mime,
      size: m.size,
      info: source.info,
      voice: m.voice,
      ...(m.durationMs ? { durationMs: m.durationMs } : {}),
      ...(m.waveform?.length ? { waveform: m.waveform } : {}),
      ...(m.round ? { round: true } : {}),
      ...(m.document ? { document: true } : {}),
      ...(m.sticker ? { sticker: true } : {}),
      ...(m.thumb ? { thumb: m.thumb } : {}),
      ...(m.width && m.height ? { width: m.width, height: m.height } : {}),
      ...(caption.trim() ? { caption: caption.trim().slice(0, 4000) } : {}),
    };
    return this.queueEncryptedFile(target, meta, { forwarded: true });
  }

  /** Edits and deletions replace the message in place (same id and seq). */
  private applyUpdate(update: MessageDto) {
    const known = this.state.messages[update.conversationId];
    // Poll updates for everyone leave out my own choice: keep the one I have.
    const prev = update.poll && update.poll.myVotes === undefined ? known?.find((x) => x.id === update.id)?.poll : undefined;
    let m = prev?.myVotes ? { ...update, poll: { ...update.poll!, myVotes: prev.myVotes } } : update;
    // The same for my answer to an event.
    const prevEvent = m.event && m.event.myRsvp === undefined ? known?.find((x) => x.id === m.id)?.event : undefined;
    if (prevEvent && prevEvent.myRsvp !== undefined) m = { ...m, event: { ...m.event!, myRsvp: prevEvent.myRsvp } };
    // And my reaction to a channel post (updates for every follower count, they never say who).
    if (m.myReaction === undefined) {
      const prevMine = known?.find((x) => x.id === m.id)?.myReaction;
      if (prevMine !== undefined) m = { ...m, myReaction: countedReactions(m) ? prevMine : null };
    }
    if (known?.some((x) => x.id === m.id)) {
      this.set({ messages: { ...this.state.messages, [m.conversationId]: mergeMessages(known, [m]) } });
    }
    const c = this.state.conversations.find((x) => x.id === m.conversationId);
    if (c?.lastMessage?.id === m.id) this.upsertConversation({ ...c, lastMessage: m });
  }

  /** A channel post's new counts (message.reactions): only on a post this device holds. */
  private applyCounts(conversationId: string, messageId: string, reactions: MessageDto['reactions']) {
    const known = this.state.messages[conversationId]?.find((x) => x.id === messageId);
    if (known) this.applyUpdate({ ...known, reactions });
    else {
      const c = this.state.conversations.find((x) => x.id === conversationId);
      if (c?.lastMessage?.id === messageId) this.upsertConversation({ ...c, lastMessage: { ...c.lastMessage, reactions } });
    }
  }

  // ---------- receipts & typing ----------

  markRead(conversationId: string): void {
    const c = this.state.conversations.find((x) => x.id === conversationId);
    if (!c || document.visibilityState !== 'visible') return;
    // Opening a chat I marked unread clears the mark (the read below clears it on the server too).
    if (c.lastSeq <= c.myLastReadSeq) {
      if (c.markedUnread) {
        this.upsertConversation({ ...c, markedUnread: false });
        void this.setChatState(conversationId, { unread: false }).catch(() => undefined);
      }
      return;
    }
    this.upsertConversation({ ...c, myLastReadSeq: c.lastSeq, unreadCount: 0, markedUnread: false });
    void api('POST', `/conversations/${conversationId}/read`, { seq: c.lastSeq }).catch(() => undefined);
  }

  setTyping(conversationId: string, typing: boolean): void {
    this.rt.command('typing', { conversationId, typing });
  }

  // ---------- server events ----------

  private applyMessage(m: MessageDto) {
    const known = this.state.messages[m.conversationId];
    // Not on hand yet (undefined: this chat's messages are not loaded, so it cannot be told).
    const fresh = known ? !known.some((x) => x.id === m.id) : undefined;
    if (known) {
      const list = mergeMessages(known, [m]);
      this.set({ messages: { ...this.state.messages, [m.conversationId]: list } });
      // A hole before it means messages were missed (a dropped socket, one overtaken on the way): fetch them.
      if (fresh) this.checkGap(m.conversationId, list, m.seq);
    } else if (this.opening.has(m.conversationId)) {
      // The chat's first page is on its way and may have been read before this one was written: kept for it.
      this.early.set(m.conversationId, [...(this.early.get(m.conversationId) ?? []), m]);
    }
    const pending = this.state.pending[m.conversationId];
    if (pending?.some((p) => p.clientMsgId === m.clientMsgId)) {
      this.setPending(m.conversationId, pending.filter((p) => p.clientMsgId !== m.clientMsgId));
    }

    const c = this.state.conversations.find((x) => x.id === m.conversationId);
    if (!c) {
      void this.loadConversations().catch(() => undefined);
      return;
    }
    // A thread reply stays in its thread: the chat's preview and unread count do not move.
    if (m.threadRootId) return;
    const mine = m.senderId === this.myId;
    if (m.seq <= c.lastSeq && c.lastMessage && c.lastMessage.seq >= m.seq) {
      // Overtaken on the way by a later one, or already in the list the server sent: whether someone else's
      // was counted cannot be told here, so the server's count is read (once for a burst).
      if (!mine && fresh !== false && m.seq > c.myLastReadSeq) this.recount(m.conversationId);
      return;
    }
    this.upsertConversation({
      ...c,
      lastSeq: Math.max(c.lastSeq, m.seq),
      lastMessage: m,
      myLastReadSeq: mine ? Math.max(c.myLastReadSeq, m.seq) : c.myLastReadSeq,
      unreadCount: mine ? c.unreadCount : c.unreadCount + 1,
    });
    if (!mine && this.activeConversationId === m.conversationId) this.markRead(m.conversationId);
    // My message in a request: how many I may still send comes from the server.
    if (mine && c.request) void this.refreshConversation(m.conversationId).catch(() => undefined);
  }

  private handle(e: ServerEvent) {
    switch (e.event) {
      case 'message.created':
        this.applyMessage(e.payload);
        if (e.payload.senderId !== this.myId) {
          this.setTypingState(e.payload.conversationId, false);
          void this.alertFor(e.payload);
        }
        return;
      case 'presence.updated':
        this.set({ presence: { ...this.state.presence, [e.payload.userId]: e.payload } });
        return;
      case 'message.updated':
        this.applyUpdate(e.payload);
        return;
      case 'message.reactions':
        this.applyCounts(e.payload.conversationId, e.payload.messageId, e.payload.reactions);
        return;
      case 'receipt.updated': {
        const c = this.state.conversations.find((x) => x.id === e.conversation_id);
        if (!c || e.payload.userId === this.myId) return;
        // Groups show "read by everyone" = minimum over members, which only the server knows.
        if (c.type === 'group') {
          void this.refreshConversation(c.id).catch(() => undefined);
          return;
        }
        this.upsertConversation({
          ...c,
          peerLastDeliveredSeq: Math.max(c.peerLastDeliveredSeq, e.payload.deliveredSeq),
          peerLastReadSeq: Math.max(c.peerLastReadSeq, e.payload.readSeq),
        });
        return;
      }
      case 'typing.updated':
        if (e.conversation_id && e.payload.userId !== this.myId) this.setTypingState(e.conversation_id, e.payload.typing);
        return;
      case 'conversation.updated':
        // Group membership may have changed: recipients are re-read before the next encrypted message.
        this.groupMembers.delete(e.payload.conversationId);
        void this.refreshConversation(e.payload.conversationId).catch(() => undefined);
        return;
      case 'learn.updated':
        this.set({ learnVersion: this.state.learnVersion + 1 });
        return;
      case 'stories.updated':
        this.set({ storiesVersion: this.state.storiesVersion + 1 });
        return;
      // Meydan: counts and my own state of a post (meydan/store.ts listens; every view shows the same numbers).
      case 'meydan.post':
        window.dispatchEvent(new CustomEvent('da:meydan-post', { detail: e.payload }));
        return;
      case 'meydan.updated':
        window.dispatchEvent(new Event('da:meydan-updated'));
        return;
      // "Seni ekleyenler" changed (someone added me, or I answered on another device): lib/contactRequests.ts.
      case 'contacts.requests':
        window.dispatchEvent(new Event('da:contact-requests'));
        return;
      // Meydan: a new post for my following feed (store.ts), and my activity page / nav dot (activity.ts).
      case 'meydan.new':
        window.dispatchEvent(new CustomEvent('da:meydan-new', { detail: e.payload }));
        return;
      case 'meydan.activity':
        window.dispatchEvent(new CustomEvent('da:meydan-activity', { detail: e.payload }));
        return;
      case 'folders.updated':
        void this.loadFolders().catch(() => undefined);
        return;
      case 'scheduled.updated':
        this.set({ scheduledVersion: this.state.scheduledVersion + 1 });
        return;
      case 'reminders.updated':
        this.set({ remindersVersion: this.state.remindersVersion + 1 });
        void this.loadReminders().catch(() => undefined);
        return;
      case 'reminder.due': {
        // The push covers installed apps and closed tabs; an open tab without push still gets a notification.
        const next = new Map(this.state.reminders);
        next.delete(e.payload.messageId);
        this.set({ reminders: next, remindersVersion: this.state.remindersVersion + 1 });
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && !('serviceWorker' in navigator && navigator.serviceWorker.controller)) {
          try {
            const n = new Notification(e.payload.title, { body: e.payload.body ?? '', tag: `remind-${e.payload.messageId}` });
            n.onclick = () => {
              window.focus();
              window.location.assign(`/messages/${e.payload.conversationId}#msg-${e.payload.messageId}`);
            };
          } catch {
            // Notifications are a convenience here.
          }
        }
        return;
      }
      case 'planner.updated':
        this.set({ plannerVersion: this.state.plannerVersion + 1 });
        return;
      case 'call.incoming':
      case 'call.answered':
      case 'call.ended':
        this.set({ callsVersion: this.state.callsVersion + 1 });
        return;
      case 'e2ee.sync':
        void this.e2ee?.sync().catch(() => undefined);
        return;
      case 'appearance.updated':
        void loadAppearance();
        return;
      case 'badges.updated':
        forgetBadges(e.payload.userId ?? undefined);
        return;
      case 'user.updated':
        // A new photo or name: every avatar of theirs reloads, and chat titles follow (debounced: a person in
        // many groups would otherwise reload the list once per change).
        refreshAvatar(e.payload.userId);
        this.set({ profileVersion: this.state.profileVersion + 1 });
        clearTimeout(this.profileReload);
        this.profileReload = setTimeout(() => void this.loadConversations().catch(() => undefined), 800);
        return;
      default:
        return;
    }
  }

  private setTypingState(conversationId: string, typing: boolean) {
    const timer = this.typingTimers.get(conversationId);
    if (timer) clearTimeout(timer);
    if (typing) {
      // Stale typing indicators expire even if the "stopped" event is lost.
      this.typingTimers.set(conversationId, setTimeout(() => this.setTypingState(conversationId, false), TYPING_TTL_MS));
    }
    if (Boolean(this.state.typing[conversationId]) !== typing) {
      this.set({ typing: { ...this.state.typing, [conversationId]: typing } });
    }
  }
}
